;(function attachTarteCurrencyFormatter(windowObject, documentObject) {
  if (windowObject.TarteCurrencyFormatter) return;

  function getCurrencyCode(explicitCurrency) {
    return (
      explicitCurrency ||
      windowObject.themeCurrency?.isoCode ||
      windowObject.Shopify?.currency?.active ||
      documentObject.documentElement?.getAttribute('data-shop-currency') ||
      documentObject.documentElement?.getAttribute('data-shopify-currency') ||
      'USD'
    );
  }

  function getLocale(locale, country) {
    const resolvedLocale = locale || windowObject.Shopify?.locale || navigator.language || 'en';
    const resolvedCountry = country || windowObject.Shopify?.country;
    if (!resolvedCountry || resolvedLocale.includes('-')) return resolvedLocale;
    return `${resolvedLocale}-${resolvedCountry}`;
  }

  function getFormatExampleNode() {
    return documentObject.getElementById('tarte-currency-format-config');
  }

  function getFormatExample(explicitExample) {
    return (
      explicitExample ||
      windowObject.__tarteFormattedCurrencyExample ||
      getFormatExampleNode()?.dataset?.formattedCurrencyExample ||
      windowObject.themeCurrency?.formattedCurrencyExample ||
      windowObject.formattedCurrencyExample ||
      documentObject.documentElement?.getAttribute('data-formatted-currency-example') ||
      ''
    );
  }

  function setFormatExample(example) {
    if (typeof example === 'string' && example.trim()) {
      windowObject.__tarteFormattedCurrencyExample = example.trim();
    }
  }

  function formatWithExample(amount, example, trimTrailingZeros) {
    const numericMatch = example.match(/[\d.,'’\s\u00A0\u202F]+/);
    if (!numericMatch) return '';

    const exampleNumeric = numericMatch[0].trim();
    let decimalSeparator = '';
    let decimalPlaces = 0;

    const trailingSeparatorMatch = exampleNumeric.match(/([^\d])(\d+)$/);
    if (trailingSeparatorMatch) {
      const trailingDigits = trailingSeparatorMatch[2].length;
      if (trailingDigits > 0 && trailingDigits < 3) {
        decimalSeparator = trailingSeparatorMatch[1];
        decimalPlaces = trailingDigits;
      }
    }

    const integerExample = decimalSeparator
      ? exampleNumeric.slice(0, exampleNumeric.lastIndexOf(decimalSeparator))
      : exampleNumeric;
    const groupingSeparatorMatch = integerExample.match(/[^\d]/);
    const groupingSeparator = groupingSeparatorMatch ? groupingSeparatorMatch[0] : '';
    const absoluteAmount = Math.abs(amount);

    const formatIntegerWithGrouping = (value) => {
      if (!groupingSeparator) return value;
      return value.replace(/\B(?=(\d{3})+(?!\d))/g, groupingSeparator);
    };

    let formattedAmount;
    if (decimalPlaces > 0 && !(trimTrailingZeros && Number.isInteger(absoluteAmount))) {
      const fixedAmount = absoluteAmount.toFixed(decimalPlaces);
      const parts = fixedAmount.split('.');
      const formattedInteger = formatIntegerWithGrouping(parts[0]);
      formattedAmount = `${formattedInteger}${decimalSeparator}${parts[1]}`;
    } else {
      formattedAmount = formatIntegerWithGrouping(Math.round(absoluteAmount).toString());
    }

    const rendered = example.replace(exampleNumeric, formattedAmount);
    return amount < 0 ? `-${rendered}` : rendered;
  }

  function formatWithIntl(amount, { currency, locale, country, trimTrailingZeros }) {
    const resolvedCurrency = getCurrencyCode(currency);
    const resolvedLocale = getLocale(locale, country);
    const absoluteAmount = Math.abs(amount);
    const baseFormatter = new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency: resolvedCurrency,
      currencyDisplay: 'narrowSymbol',
    });
    const { maximumFractionDigits: currencyFractionDigits = 2 } = baseFormatter.resolvedOptions();

    const minimumFractionDigits = trimTrailingZeros
      ? (currencyFractionDigits === 0 || Number.isInteger(absoluteAmount) ? 0 : currencyFractionDigits)
      : currencyFractionDigits;

    const formatted = new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency: resolvedCurrency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits,
      maximumFractionDigits: currencyFractionDigits,
    }).format(absoluteAmount);

    return amount < 0 ? `-${formatted}` : formatted;
  }

  function formatAmount(amount, options = {}) {
    const numericAmount = typeof amount === 'number' ? amount : Number(amount);
    if (!Number.isFinite(numericAmount)) return options.fallback || '';

    const formatExample = getFormatExample(options.formatExample);
    if (formatExample) {
      const formattedFromExample = formatWithExample(
        numericAmount,
        formatExample,
        options.trimTrailingZeros === true,
      );
      if (formattedFromExample) return formattedFromExample;
    }

    try {
      return formatWithIntl(numericAmount, options);
    } catch (error) {
      console.warn('Failed to format currency value', error);
      return options.fallback || `${getCurrencyCode(options.currency)} ${numericAmount}`;
    }
  }

  function formatCents(valueInCents, options = {}) {
    const numericCents = typeof valueInCents === 'number' ? valueInCents : Number(valueInCents);
    if (!Number.isFinite(numericCents)) return options.fallback || '';
    return formatAmount(numericCents / 100, options);
  }

  windowObject.TarteCurrencyFormatter = {
    formatAmount,
    formatCents,
    setFormatExample,
  };
})(window, document);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));
  
  if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

document.querySelectorAll('details.mega-menu').forEach((detailsEl) => {
  const parentEl = detailsEl.closest('.header');
  const stickyParentEl = parentEl?.parentElement;
  const promoBarEls = document.querySelectorAll('.announcement-bar-section, .utility-bar--announcement');

  if (!parentEl) return;

  detailsEl.addEventListener('mouseenter', () => {
    parentEl.classList.add('hover--megamenu');
    if (stickyParentEl) {
      stickyParentEl.classList.add('hover--megamenu');
    }

    if (promoBarEls.length) {
      promoBarEls.forEach((el) => {
        el.classList.add('hover--megamenu');
      });
    }
  });

  detailsEl.addEventListener('mouseleave', () => {
    parentEl.classList.remove('hover--megamenu');

    if (stickyParentEl) {
      stickyParentEl.classList.remove('hover--megamenu');
    }

    if (promoBarEls.length) {
      promoBarEls.forEach((el) => {
        el.classList.remove('hover--megamenu');
      });
    }
  });
});

const loyaltyAccountLink = document.querySelector('.loyalty-wrapper .loyalty-sign-in-link.header__icon--account');
const loyaltyAccountMenu = document.querySelector('.loyalty-wrapper .account-nav-menu');
const loyaltyDesktopMediaQuery = window.matchMedia('(min-width: 1024px)');

let hideAccountMenuTimeout;

function showAccountMenu() {
  clearTimeout(hideAccountMenuTimeout);
  loyaltyAccountMenu?.classList.add('account-menu--visible');
}

function hideAccountMenu() {
  hideAccountMenuTimeout = setTimeout(() => {
    loyaltyAccountMenu?.classList.remove('account-menu--visible');
  }, 300);
}

function hideAccountMenuImmediately() {
  clearTimeout(hideAccountMenuTimeout);
  loyaltyAccountMenu?.classList.remove('account-menu--visible');
}

function getAccountMenuFocusableElements() {
  if (!loyaltyAccountMenu) return [];
  return Array.from(
    loyaltyAccountMenu.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => !element.hasAttribute('disabled'));
}

loyaltyAccountLink?.addEventListener('mouseenter', showAccountMenu);
loyaltyAccountLink?.addEventListener('mouseleave', hideAccountMenu);
loyaltyAccountMenu?.addEventListener('mouseenter', showAccountMenu);
loyaltyAccountMenu?.addEventListener('mouseleave', hideAccountMenu);
loyaltyAccountLink?.addEventListener('keydown', (event) => {
  if (!loyaltyDesktopMediaQuery.matches) return;

  if (event.key === 'Tab' && !event.shiftKey) {
    const [firstMenuItem] = getAccountMenuFocusableElements();
    if (!firstMenuItem) return;
    event.preventDefault();
    showAccountMenu();
    firstMenuItem.focus();
  }

  if (event.key === 'ArrowDown') {
    const [firstMenuItem] = getAccountMenuFocusableElements();
    if (!firstMenuItem) return;
    event.preventDefault();
    showAccountMenu();
    firstMenuItem.focus();
  }
});
loyaltyAccountLink?.addEventListener('focusout', () => {
  hideAccountMenuTimeout = setTimeout(() => {
    if (
      loyaltyAccountMenu &&
      !loyaltyAccountMenu.contains(document.activeElement) &&
      document.activeElement !== loyaltyAccountLink
    ) {
      hideAccountMenuImmediately();
    }
  }, 0);
});
loyaltyAccountMenu?.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  hideAccountMenuImmediately();
  loyaltyAccountLink?.focus();
});
loyaltyAccountMenu?.addEventListener('focusout', () => {
  hideAccountMenuTimeout = setTimeout(() => {
    if (
      loyaltyAccountMenu &&
      !loyaltyAccountMenu.contains(document.activeElement) &&
      document.activeElement !== loyaltyAccountLink
    ) {
      hideAccountMenuImmediately();
    }
  }, 0);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function closeAllModalDialogs() {
  document.querySelectorAll('modal-dialog').forEach((modalDialog) => {if(modalDialog.hasAttribute('open') && modalDialog.hide){modalDialog.hide()}});
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
     this.lineItemSrc = null;
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this && !event.target.classList.contains('vto-window')) { // do not close VTO modal on background click, VTO user must click X button to exit VTO
          this.hide();
        }

      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    window.closeAllModalDialogs();
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    const scrollY = window._savedScrollPosition || window.scrollY;
    document.body.classList.add('overflow-hidden');

    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });

    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    if (popup) popup.loadContent();
    setTimeout(() => {
    trapFocus(this, this.querySelector('[role="dialog"]'));
    }, 300);
    window.pauseAllMedia();
  }

  hide() {
    // Preserve edit cart context during modal chain navigation
    if (!window.qvCartItemKey) {
      window.isFromQv = false;
      window.isFromQvCart = false;
      window.isFromPDP = false;
    }
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed', {detail: this.id}));
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    const qvModal = document.querySelector('#Quickshop-Modal');
    if(window.isFromQv && qvModal) {
      window.isFromQv = false;
      qvModal.show();
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

class FetchModalDialog extends ModalDialog {
  constructor() {
    super();
  }

  connectedCallback() {
    if (window.Shopify && window.Shopify.designMode) {
      super.show()
    }
  }

  show(opener) {
    super.show(opener)
  }

  hide() {
    super.hide();
  }
}
customElements.define('fetch-modal-dialog', FetchModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const openedFromQuickshop = !!button.closest('#Quickshop-Modal');
      const openedFromQuickshopCart = !!button.closest('#Quickshop-Modal-cart');
      const openedFromBuildkitQuickshop = !!button.closest('#Buildkit-Quickshop-Modal');
      const modalSelector = this.getAttribute('data-modal') || '';
      const openedShadeListFromQuickshop = openedFromQuickshop && modalSelector.startsWith('#ShadeList-Modal');
      const openedShadeListFromQuickshopCart = openedFromQuickshopCart && modalSelector.startsWith('#ShadeList-Modal');

      if (openedShadeListFromQuickshop) {
        // Keep quickshop content intact while shade list temporarily steals focus
        window.skipQuickshopReset = true;
      }

      const modal = document.querySelector(this.getAttribute('data-modal'));

      // For shade list modals, capture the source context BEFORE modal.show() clears state
      if (modal && modalSelector.startsWith('#ShadeList-Modal')) {
        if (!modal.dataset.capturedSource) {
          let capturedSource = 'pdp';
          if (openedFromQuickshopCart) {
            const qvSource = window.bagSpecialQuickviewState?.source;
            capturedSource = (qvSource === 'recommended' || qvSource === 'checkout special') ? qvSource : '';
          } else if (openedFromQuickshop || openedFromBuildkitQuickshop || button.closest('#CustomKit-Quickshop-Modal')) {
            const qvSource = window.bagSpecialQuickviewState?.source || button.closest('product-info')?.dataset?.source;
            capturedSource = (['recommended', 'checkout special', 'tarte_gwp'].includes(qvSource)) ? qvSource : 'pdp';
          }
          modal.dataset.capturedSource = capturedSource;
        }
      }
      const kitQuickview = modalSelector.startsWith('#IngredientsList-Modal') ? button.closest('#Buildkit-Quickshop-Modal, #CustomKit-Quickshop-Modal') : null;
      const savedBagState = kitQuickview ? window.bagSpecialQuickviewState : null;
      if (modal) modal.show(button);
      if (modal && kitQuickview && !this.reopenKitQuickviewPending) {
        // show() closes the kit quickview, so bring it back when the ingredients drawer closes
        this.reopenKitQuickviewPending = true;
        const reopenKitQuickview = (event) => {
          if (event.detail !== modal.id) return;
          document.body.removeEventListener('modalClosed', reopenKitQuickview);
          this.reopenKitQuickviewPending = false;
          // The kit quickview's modalClosed reset cleared this, so restore it before reopening
          window.bagSpecialQuickviewState = savedBagState;
          requestAnimationFrame(() => kitQuickview.show());
        };
        document.body.addEventListener('modalClosed', reopenKitQuickview);
      }
      if (openedFromQuickshop) {
        window.isFromQv = true;
        window.isFromQvCart = false;
        window.isFromPDP = false;
        window.isFromQvBuildkit = false;
        window.isFromQvCustomkit = false;
      } else if (openedFromQuickshopCart) {
        window.isFromQvCart = true;
        window.isFromQv = false;
        window.isFromPDP = false;
        window.isFromQvBuildkit = false;
        window.isFromQvCustomkit = false;
        
        if (openedShadeListFromQuickshopCart && modal) {
          const cartChecked =
            modal.closest('#quickshop-cart')?.querySelector('input.shade-input:checked') ||
            document.querySelector('#quickshop-cart input.shade-input:checked') ||
            document.querySelector('#quickshop-cart input.bundle-swatch:checked');
          const variantId = cartChecked?.getAttribute('data-variant-id');

          if (variantId) {
            const modalInput = modal.querySelector(`input[data-variant-id="${variantId}"]`);
            if (modalInput) {
              modalInput.checked = true;
              modalInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }

      } else if (openedFromBuildkitQuickshop) {
        window.isFromQvCart = false;
        window.isFromQv = false;
        window.isFromPDP = false;
        window.isFromQvBuildkit = true;
        window.isFromQvCustomkit = false;
      } else if (button.closest('#CustomKit-Quickshop-Modal')) {
        window.isFromQvCart = false;
        window.isFromQv = false;
        window.isFromPDP = false;
        window.isFromQvBuildkit = false;
        window.isFromQvCustomkit = true;
      } else {
        window.isFromPDP = true;
        window.isFromQv = false;
        window.isFromQvCart = false;
        window.isFromQvBuildkit = false;
        window.isFromQvCustomkit = false;
      }
    });
  }
}
customElements.define('modal-opener', ModalOpener);

// Kit quickviews clone only the PDP details, so add the source PDP's ingredients drawer under its own id and point the cloned trigger at it
window.syncIngredientsModal = (sourceDoc, container) => {
  document.getElementById('IngredientsList-Modal-qv')?.remove();
  const source = sourceDoc?.querySelector('#IngredientsList-Modal');
  if (!source) return;
  const drawer = document.importNode(source, true);
  drawer.id = 'IngredientsList-Modal-qv';
  document.body.appendChild(drawer);
  container?.querySelectorAll('modal-opener[data-modal="#IngredientsList-Modal"]').forEach((opener) => opener.setAttribute('data-modal', '#IngredientsList-Modal-qv'));
};

class FetchModalOpener extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.backButtonContext = null;
    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => { 
      const modalID = this.getAttribute('data-modal');
      const url = this.getAttribute('data-url');
      const backButtonContext = button.getAttribute('data-back-button-context');
      if (backButtonContext) {
        this.backButtonContext = this.closest('#findMyShadeModal').querySelector(backButtonContext); // button.closest(backButtonContext);
        button.removeAttribute('data-back-button-context');
      }
      if(button.closest('#Quickshop-Modal-cart')) {
        window.isFromQvCart = true;
      } else if(button.closest('#Quickshop-Modal')) {
        window.isFromQv = true;
        window.isFromQvCart = false;
        window.qvCartItemKey = null;
      } else if(button.closest('#findMyShadeModal')) {
        // Opening VtoModal from findMyShadeModal - preserve context established earlier
      } else {
        // Opening from PDP or other non-modal context - clear stale edit cart context
        window.isFromQvCart = false;
        window.qvCartItemKey = null;
      }
      this.renderModal(url, modalID);
    });
  }

  renderModal(url, modalID) {
    let modalDialog;
    let modal = document.querySelector(modalID);
    if(modal && modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        modalDialog = html.querySelector(modalID);
        const modalScripts = modalDialog.querySelectorAll('script');
        if (modalScripts.length) {
          modalScripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
            } else {
              newScript.textContent = script.textContent;
            }
            document.body.appendChild(newScript);
          });
        }

        document.body.appendChild(modalDialog);
        
      })
      .then(() => {
        if (this.backButtonContext) {
          modalDialog.show(this.backButtonContext);
          this.backButtonContext;
        } else {
          modalDialog.show(this);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }
}
customElements.define('fetch-modal-opener', FetchModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider || !this.sliderItems?.length) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    setTimeout(() => {
      if (currentSlide && nextSlide) {
        currentSlide.classList.remove(`${animationClassOut}-${direction}`);
        nextSlide.classList.remove(`${animationClassIn}-${direction}`);
      }
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

function getBundleAvailabilityScope(element) {
  return element?.closest('product-info') ||
    element?.closest('#quickshop-cart') ||
    element?.closest('#quickshop') ||
    element?.closest('.product__info-wrapper') ||
    document;
}

function getBundleSwatchVariantId(input) {
  return String(input?.dataset?.variantId || input?.getAttribute('bundle-variant-id') || '').trim();
}

function normalizeBundleAvailabilityValue(value) {
  const normalizedValue = String(value).trim().toLowerCase();
  if (value === true || normalizedValue === 'true') return 'true';
  if (value === false || normalizedValue === 'false') return 'false';
  return '';
}

function setBundleSwatchBaseAvailability(input, available) {
  const normalizedAvailability = normalizeBundleAvailabilityValue(available);
  if (!normalizedAvailability) return;

  input.dataset.bundleBaseVariantAvailable = normalizedAvailability;
  input.dataset.bundleShopifyVariantAvailable = normalizedAvailability;
}

function getBundleSwatchBaseAvailability(input, variant = null) {
  const variantAvailability = normalizeBundleAvailabilityValue(variant?.available);
  if (variantAvailability) {
    setBundleSwatchBaseAvailability(input, variantAvailability);
    return variantAvailability === 'true';
  }

  const persistedAvailability = normalizeBundleAvailabilityValue(
    input.dataset.bundleShopifyVariantAvailable ||
    input.dataset.bundleBaseVariantAvailable ||
    input.dataset.variantAvailable
  );

  return persistedAvailability === 'true';
}

function getBundleContainerVariants(container) {
  const variantsScript = container.querySelector('script[data-bundle-variants]');
  if (!variantsScript) return [];

  try {
    const variants = JSON.parse(variantsScript.textContent);
    return Array.isArray(variants) ? variants : [];
  } catch (error) {
    console.error('Unable to parse bundle variants for inventory availability:', error);
    return [];
  }
}

function getBundleSwatchCandidateVariant(input, container) {
  const variants = getBundleContainerVariants(container);
  if (!variants.length) return null;

  const selectedByIndex = {};
  container.querySelectorAll('input.bundle-swatch[type="radio"]:checked').forEach(swatch => {
    const optionIndex = Number(swatch.dataset.optionIndex);
    if (Number.isFinite(optionIndex)) {
      selectedByIndex[optionIndex] = swatch.dataset.optionValue;
    }
  });

  const candidateOptionIndex = Number(input.dataset.optionIndex);
  if (Number.isFinite(candidateOptionIndex)) {
    selectedByIndex[candidateOptionIndex] = input.dataset.optionValue;
  }

  const selectedEntries = Object.entries(selectedByIndex).filter(([, value]) => value);
  if (!selectedEntries.length) return null;

  return variants.find(variant =>
    selectedEntries.every(([idx, value]) => variant[`option${Number(idx) + 1}`] === value)
  ) || null;
}

function getBundleSwatchInventoryLimit(input, variant = null) {
  if (!getBundleSwatchBaseAvailability(input, variant)) return 0;

  const inventoryPolicy = String(variant?.inventory_policy ?? input.dataset.variantInventoryPolicy ?? '').toLowerCase();
  const inventoryManagement = String(variant?.inventory_management ?? input.dataset.variantInventoryManagement ?? '').toLowerCase();
  if (inventoryPolicy === 'continue' || inventoryManagement !== 'shopify') return Infinity;

  const rawInventoryQuantity = variant?.inventory_quantity ?? input.dataset.variantInventoryQuantity;
  if (rawInventoryQuantity === undefined || rawInventoryQuantity === '') return Infinity;

  const inventoryQuantity = Number(rawInventoryQuantity);
  return Number.isFinite(inventoryQuantity) ? Math.max(inventoryQuantity, 0) : Infinity;
}

function getTarteVariantIdFromValue(value) {
  if (value == null || value === '') return null;

  const numericId = Number(value);
  if (Number.isFinite(numericId)) return numericId;

  const match = String(value).match(/ProductVariant\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function parseTarteBundleComponentsProperty(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function getTarteCartItemBundleKey(item) {
  return item?.properties?._bundle_key || item?.bundle_key || null;
}

function getTarteCartItemComponentVariantId(component) {
  return getTarteVariantIdFromValue(
    component?.variant_id ||
    component?.variantId ||
    component?.merchandiseId ||
    component?.merchandise?.id ||
    component?.id ||
    component?.variant?.id
  );
}

function incrementTarteVariantCount(counts, variantId, quantity = 1) {
  const id = getTarteVariantIdFromValue(variantId);
  const qty = Math.max(0, Number(quantity) || 0);
  if (!id || qty <= 0) return;

  const key = String(id);
  counts.set(key, (counts.get(key) || 0) + qty);
}

function getTarteCartLineKeyToExclude(scope) {
  const root = scope || document;
  const isQuickshopCartScope =
    root?.id === 'quickshop-cart' ||
    !!root?.closest?.('#quickshop-cart');

  if (isQuickshopCartScope) {
    const quickshopCart = root?.id === 'quickshop-cart'
      ? root
      : root?.closest?.('#quickshop-cart');
    const quickshopCartModal = root?.id === 'Quickshop-Modal-cart'
      ? root
      : root?.closest?.('#Quickshop-Modal-cart');
    const cartItemKey =
      window.qvCartItemKey ||
      root?.dataset?.cartItemKey ||
      quickshopCart?.dataset?.cartItemKey ||
      quickshopCartModal?.dataset?.cartItemKey;

    if (cartItemKey) return cartItemKey;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('bk_line_key') || searchParams.get('ck_line_key');
  } catch (error) {
    return null;
  }
}

function getTarteBundleEntrySlotKey(input) {
  const container = input?.closest?.('.bundle-variant-selects[data-bundle-slot]');
  if (!container?.dataset?.bundleSlot) return null;

  return `${container.dataset.bundleSlot}::${container.dataset.productHandle || ''}`;
}

function getTarteEditedBundleComponentSlotMap(lineKey) {
  if (!lineKey) return new Map();

  const row = Array.from(document.querySelectorAll('.cart-item[data-item-key]'))
    .find(item => item.dataset.itemKey === lineKey);
  const components = parseTarteBundleComponentsProperty(row?.dataset?.bundleComponents);
  const slotsByVariant = new Map();

  components.forEach((component, index) => {
    const variantId = getTarteCartItemComponentVariantId(component);
    if (!variantId) return;

    const slot = String(component?.slot || component?.bundleSlot || component?.bundle_slot || index + 1);
    if (!slot) return;

    const productHandle = String(component?.productHandle || component?.product_handle || '');
    const key = String(variantId);
    const slots = slotsByVariant.get(key) || new Set();
    slots.add(`${slot}::${productHandle}`);
    if (productHandle) slots.add(`${slot}::`);
    slotsByVariant.set(key, slots);
  });

  return slotsByVariant;
}

function getTarteCartLineBundleKey(cartItems, lineKey) {
  if (!lineKey || !Array.isArray(cartItems)) return null;
  const line = cartItems.find(item => item?.key === lineKey);
  return getTarteCartItemBundleKey(line);
}

function buildTarteCartVariantQuantityMap(cartData, options = {}) {
  const counts = new Map();
  const cartItems = Array.isArray(cartData?.items) ? cartData.items : [];
  const excludedLineKey = options.excludeLineKey || null;
  const excludedBundleKey = options.excludeBundleKey || getTarteCartLineBundleKey(cartItems, excludedLineKey);

  cartItems.forEach(item => {
    const itemBundleKey = getTarteCartItemBundleKey(item);
    if (excludedLineKey && item?.key === excludedLineKey) return;
    if (excludedBundleKey && itemBundleKey === excludedBundleKey) return;

    incrementTarteVariantCount(counts, item?.variant_id, item?.quantity || 1);

    if (Array.isArray(item?.item_components) && item.item_components.length) {
      item.item_components.forEach(component => {
        incrementTarteVariantCount(
          counts,
          getTarteCartItemComponentVariantId(component),
          component?.quantity || 1
        );
      });
      return;
    }

    const bundleComponents = parseTarteBundleComponentsProperty(item?.properties?._bundle_components);
    if (bundleComponents.length) {
      const lineQuantity = Math.max(1, Number(item?.quantity) || 1);
      bundleComponents.forEach(component => {
        const componentQuantity = Math.max(1, Number(component?.quantity) || 1);
        incrementTarteVariantCount(
          counts,
          getTarteCartItemComponentVariantId(component),
          componentQuantity * lineQuantity
        );
      });
      return;
    }
  });

  return counts;
}

window.buildTarteCartVariantQuantityMap = buildTarteCartVariantQuantityMap;

function getTarteVariantQuantityFromMap(counts, variantId) {
  const id = getTarteVariantIdFromValue(variantId);
  return id ? counts.get(String(id)) || 0 : 0;
}

async function getBundleCartVariantQuantities(scope, options = {}) {
  try {
    const forceFetch = options.forceFetch !== undefined ? options.forceFetch : true;
    const cart = typeof window.getCurrentTarteCartData === 'function'
      ? await window.getCurrentTarteCartData({ forceFetch })
      : await fetch('/cart.js', { cache: 'no-store' }).then(res => res.json());

    return buildTarteCartVariantQuantityMap(cart, {
      excludeLineKey: options.excludeLineKey || getTarteCartLineKeyToExclude(scope),
    });
  } catch (error) {
    console.error('Unable to load cart data for bundle inventory availability:', error);
    return new Map();
  }
}

function setBundleSwatchAvailability(input, available, message = '') {
  input.dataset.variantAvailable = available ? 'true' : 'false';
  input.classList.toggle('visually-disabled', !available);
  input.classList.toggle('shade--not-available', !available);

  if (available) {
    input.removeAttribute('data-unavailable-message');
  } else if (message) {
    input.setAttribute('data-unavailable-message', message);
  }
}

function getSelectedBundleAvailability(scope) {
  const root = scope || document;
  return Array.from(root.querySelectorAll('.non-bundle-input:checked, fieldset input:checked')).map(
    ({ dataset }) => dataset.variantAvailable
  );
}

function getSelectedBundleDiscontinued(scope) {
  const root = scope || document;
  return Array.from(root.querySelectorAll('.non-bundle-input:checked, fieldset input:checked')).map(
    ({ dataset }) => dataset.variantDiscontinued
  );
}

function syncBundleProductInfoState(scope) {
  const root = scope || document;
  const productInfo = root?.matches?.('product-info')
    ? root
    : root?.closest?.('product-info') || root?.querySelector?.('product-info');

  if (typeof productInfo?.disableBundleToggle === 'function') {
    productInfo.disableBundleToggle(
      getSelectedBundleAvailability(productInfo),
      getSelectedBundleDiscontinued(productInfo)
    );
  }
}

function refreshSelectedBundleShadeValues(scope) {
  const root = scope || document;
  const scopedContainer = root?.matches?.('.bundle-variant-selects[data-product-handle][data-bundle-slot]')
    ? root
    : null;
  const bundleContainers = scopedContainer
    ? [scopedContainer]
    : Array.from(root.querySelectorAll?.('.bundle-variant-selects[data-product-handle][data-bundle-slot]') || []);

  bundleContainers.forEach(container => {
    const variantSelects = container.matches?.('variant-selects')
      ? container
      : container.closest?.('variant-selects');

    if (typeof variantSelects?.updateBundleImageDesc !== 'function') return;

    container.querySelectorAll('input.bundle-swatch:checked').forEach(input => {
      variantSelects.updateBundleImageDesc(input);
    });
  });
}

window.hasUnavailableSelectedBundleInput = function(scope = document) {
  return getSelectedBundleAvailability(scope).some(available => available === 'false');
};

window.updateDuplicateBundleSwatchAvailability = async function(scope = document, options = {}) {
  const root = scope || document;
  const bundleContainers = Array.from(root.querySelectorAll('.bundle-variant-selects[data-product-handle][data-bundle-slot]'));
  if (!bundleContainers.length) return false;

  const requestToken = {};
  root.__bundleAvailabilityRequestToken = requestToken;
  const deductCartQuantities = options.deductCartQuantities !== false;
  const excludedLineKey = options.excludeLineKey || getTarteCartLineKeyToExclude(root);
  const isEditingCartLine = Boolean(excludedLineKey);
  const editedComponentSlotsByVariant = isEditingCartLine
    ? getTarteEditedBundleComponentSlotMap(excludedLineKey)
    : new Map();
  const cartVariantQuantities = deductCartQuantities
    ? await getBundleCartVariantQuantities(root, {
        ...options,
        excludeLineKey: excludedLineKey,
        forceFetch: isEditingCartLine ? true : options.forceFetch,
      })
    : {};
  if (root.__bundleAvailabilityRequestToken !== requestToken) return false;

  const orderedContainers = bundleContainers.slice().sort((a, b) =>
    (Number(a.dataset.bundleSlot) || 0) - (Number(b.dataset.bundleSlot) || 0)
  );
  const inputsByVariant = {};

  orderedContainers.forEach(container => {
    container.querySelectorAll('input.bundle-swatch').forEach(input => {
      const variant = getBundleSwatchCandidateVariant(input, container);
      const variantId = String(variant?.id || getBundleSwatchVariantId(input));
      if (!variantId) return;
      inputsByVariant[variantId] = inputsByVariant[variantId] || [];
      inputsByVariant[variantId].push({
        input,
        variant,
        baseAvailable: getBundleSwatchBaseAvailability(input, variant),
      });
    });
  });

  Object.entries(inputsByVariant).forEach(([variantId, entries]) => {
    const baseUnavailableEntries = entries.filter(entry => !entry.baseAvailable);
    const baseAvailableEntries = entries.filter(entry => entry.baseAvailable);
    const editedComponentSlots = editedComponentSlotsByVariant.get(String(variantId));
    const limit = Math.min(
      ...baseAvailableEntries.map(entry => getBundleSwatchInventoryLimit(entry.input, entry.variant))
    );
    const oosMessage = entries[0]?.input.closest('.bundle-variant-selects')?.dataset?.oosMessage || '';

    baseUnavailableEntries.forEach(({ input }) => setBundleSwatchAvailability(input, false, oosMessage));

    if (!baseAvailableEntries.length || !Number.isFinite(limit)) {
      baseAvailableEntries.forEach(({ input }) => setBundleSwatchAvailability(input, true, oosMessage));
      return;
    }

    const cartReservedCount = deductCartQuantities
      ? getTarteVariantQuantityFromMap(cartVariantQuantities, variantId)
      : 0;
    const availableSlots = Math.max(0, limit - cartReservedCount);
    const checkedEntries = baseAvailableEntries.filter(({ input }) => input.checked);
    const uncheckedEntries = baseAvailableEntries.filter(({ input }) => !input.checked);
    const checkedGroups = [];
    const checkedGroupsBySlot = new Map();

    checkedEntries.forEach(entry => {
      const slot = entry.input.closest('.bundle-variant-selects') || entry.input;

      if (!checkedGroupsBySlot.has(slot)) {
        const group = [];
        checkedGroupsBySlot.set(slot, group);
        checkedGroups.push(group);
      }

      checkedGroupsBySlot.get(slot).push(entry);
    });

    const allowedInputs = new Set();
    let allowedGroupCount = 0;
    const allowGroup = (group) => {
      if (allowedGroupCount >= availableSlots) return;
      group.forEach(({ input }) => allowedInputs.add(input));
      allowedGroupCount += 1;
    };

    if (editedComponentSlots?.size) {
      const editedGroups = [];
      const editedGroupsBySlot = new Map();

      baseAvailableEntries.forEach(entry => {
        const slotKey = getTarteBundleEntrySlotKey(entry.input);
        if (!slotKey || !editedComponentSlots.has(slotKey)) return;

        const slot = entry.input.closest('.bundle-variant-selects') || entry.input;
        if (!editedGroupsBySlot.has(slot)) {
          const group = [];
          editedGroupsBySlot.set(slot, group);
          editedGroups.push(group);
        }

        editedGroupsBySlot.get(slot).push(entry);
      });

      editedGroups.forEach(allowGroup);
    }

    checkedGroups.slice(0, availableSlots).forEach(group => {
      if (group.some(({ input }) => allowedInputs.has(input))) return;
      allowGroup(group);
    });

    uncheckedEntries
      .filter(({ input }) => !allowedInputs.has(input))
      .slice(0, Math.max(0, availableSlots - allowedGroupCount))
      .forEach(({ input }) => allowedInputs.add(input));

    baseAvailableEntries.forEach(({ input }) => {
      setBundleSwatchAvailability(input, allowedInputs.has(input), oosMessage);
    });
  });

  refreshSelectedBundleShadeValues(root);
  syncBundleProductInfoState(root);
  return window.hasUnavailableSelectedBundleInput(root);
};

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.pickerType = this.dataset?.pickerType;
    this.updateBundleImageDesc();
    this.selectedOptionsMap = new Map();
  }

  connectedCallback() {
    this.addEventListener('change', async (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      this.updateBundleOptionAvailability(target);
      if (this.pickerType === 'bundle-picker') {
        await this.updateDuplicateBundleSwatchAvailability({ forceFetch: false, deductCartQuantities: false });
      }
      this.updateBundleImageDesc();
      this.updateRecommendedProductsCTA(target);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
          selectedVariantInventory: this.selectedVariantInventory,
          selectedVariantDiscontinued: this.selectedVariantDiscontinued
        },
      });
    });

    if (this.pickerType === 'upsell-variant-picker') {
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        this.updateUpsellDesc(target);
        this.updateUpsellCTA(target);
        this.updateUpsellImage(target);
      });
    }

    const autoChecked = this.querySelector('input[type=radio]:checked') || this.querySelector('input[data-initially-checked="true"]');
    if (autoChecked) {
      const optionName = autoChecked.name;
      const valueId = autoChecked.dataset.optionValueId;
      if (optionName && valueId) {
        this.selectedOptionsMap.set(optionName, valueId);
      }
    }

    if (this.pickerType === 'bag-special-variant-picker') {
      this.ensureInputSelected();
    }

    if (this.pickerType === 'bundle-picker') {
      this.initializeBundleSwatchBaseAvailability();
      this.updateDuplicateBundleSwatchAvailability({ forceFetch: false, deductCartQuantities: false });
      this.updateBundleImageDesc();
    }

  }

  initializeBundleSwatchBaseAvailability() {
    this.querySelectorAll('input.bundle-swatch').forEach(input => {
      const variant = getBundleSwatchCandidateVariant(input, input.closest('.bundle-variant-selects') || this);

      if (variant?.available !== undefined) {
        setBundleSwatchBaseAvailability(input, variant.available);
        return;
      }

      if (!input.dataset.bundleBaseVariantAvailable) {
        input.dataset.bundleBaseVariantAvailable = input.dataset.variantAvailable || 'false';
      }

      if (!input.dataset.bundleShopifyVariantAvailable) {
        input.dataset.bundleShopifyVariantAvailable = input.dataset.bundleBaseVariantAvailable;
      }
    });
  }

  updateDuplicateBundleSwatchAvailability(options = {}) {
    return window.updateDuplicateBundleSwatchAvailability(getBundleAvailabilityScope(this), options);
  }

  updateBundleImageDesc(target = null) {
    let inputs = [];
  
    if (target) {
      inputs = [target];
    } else {
      inputs = this.matches('.bundle-variant-selects')
        ? this.querySelectorAll('.shade-input:checked')
        : this.querySelectorAll('.bundle-variant-selects .shade-input:checked');
    }
  
    inputs.forEach(input => {
      const wrapper = input.closest('.bundle-product-container');
      if (!wrapper) return;
  
      const mainImageSelector = `#${input.getAttribute('data-main-image')}`;
      const shadeDescSelector = `#${input.getAttribute('data-short-desc')}`;
      const newImageUrl = input.getAttribute('data-variant-image');
      const newShadeDesc = input.getAttribute('data-variant-desc');
      const unavailableMessage = input.getAttribute('data-unavailable-message');
      let newShadeValue = input.getAttribute('value');

      const mainImages = wrapper.querySelectorAll(mainImageSelector);
      const shadeDescs = wrapper.querySelectorAll(shadeDescSelector);
      const fieldset = input.closest('fieldset');
      const shadeValueContainer = fieldset
        ? fieldset.querySelector('.product-form__shade-value')
        : wrapper.querySelector('.product-form__shade-value');

      if (unavailableMessage) {
        shadeValueContainer?.classList?.add('has-oos-msg');
        newShadeValue = '<span class="shade-value--bundle">' + newShadeValue + '</span> <span class="shade-value--not-available">' + unavailableMessage;
      } else {
        shadeValueContainer?.classList?.remove('has-oos-msg');
        newShadeValue = '<span class="shade-value--bundle">' + newShadeValue;
      }

      if (newImageUrl && mainImages.length) {
        mainImages.forEach(mainImage => {
          mainImage.src = newImageUrl;
        });
      }

      if (newShadeValue && shadeValueContainer) {
        shadeValueContainer.innerHTML = newShadeValue + ' <span class="product-shade-icon--angle-down"><i class="fa-light fa-angle-down"></i></span>';
      }

      if (newShadeDesc && shadeDescs.length) {
        shadeDescs.forEach(shadeDesc => {
          shadeDesc.innerHTML = newShadeDesc;
        });
      }
    });
  }

  updateBundleOptionAvailability(changedInput) {
    if (!changedInput || !changedInput.classList.contains('bundle-swatch')) return;

    const variantsScript = this.querySelector('script[data-bundle-variants]');
    if (!variantsScript) return;

    const variants = JSON.parse(variantsScript.textContent);
    const oosMessage = this.dataset.oosMessage || '';
    const changedOptionIndex = parseInt(changedInput.dataset.optionIndex);

    const allBundleSwatches = this.querySelectorAll('input.bundle-swatch[type="radio"]');

    const selectedByIndex = {};
    allBundleSwatches.forEach(swatch => {
      if (swatch.checked) {
        selectedByIndex[parseInt(swatch.dataset.optionIndex)] = swatch.dataset.optionValue;
      }
    });

    const otherOptionIndices = [...new Set(
      Array.from(allBundleSwatches)
        .map(s => parseInt(s.dataset.optionIndex))
        .filter(idx => idx !== changedOptionIndex)
    )];

    otherOptionIndices.forEach(optionIdx => {
      const groupSwatches = Array.from(allBundleSwatches).filter(
        s => parseInt(s.dataset.optionIndex) === optionIdx
      );

      groupSwatches.forEach(swatch => {
        const candidate = { ...selectedByIndex, [optionIdx]: swatch.dataset.optionValue };

        const hasAvailableVariant = variants.some(v =>
          Object.entries(candidate).every(
            ([idx, val]) => v[`option${parseInt(idx) + 1}`] === val
          ) && v.available
        );

        if (hasAvailableVariant) {
          swatch.classList.remove('visually-disabled', 'shade--not-available');
          setBundleSwatchBaseAvailability(swatch, true);
          swatch.dataset.variantAvailable = 'true';
          swatch.removeAttribute('data-unavailable-message');
        } else {
          swatch.classList.add('visually-disabled', 'shade--not-available');
          setBundleSwatchBaseAvailability(swatch, false);
          swatch.dataset.variantAvailable = 'false';
          if (oosMessage) swatch.setAttribute('data-unavailable-message', oosMessage);
        }
      });
    });

    const changedGroupSwatches = Array.from(allBundleSwatches).filter(
      s => parseInt(s.dataset.optionIndex) === changedOptionIndex
    );
    changedGroupSwatches.forEach(swatch => {
      const candidate = { ...selectedByIndex, [changedOptionIndex]: swatch.dataset.optionValue };
      const hasAvailableVariant = variants.some(v =>
        Object.entries(candidate).every(
          ([idx, val]) => v[`option${parseInt(idx) + 1}`] === val
        ) && v.available
      );
      if (hasAvailableVariant) {
        swatch.classList.remove('visually-disabled', 'shade--not-available');
        setBundleSwatchBaseAvailability(swatch, true);
        swatch.dataset.variantAvailable = 'true';
        swatch.removeAttribute('data-unavailable-message');
      } else {
        swatch.classList.add('visually-disabled', 'shade--not-available');
        setBundleSwatchBaseAvailability(swatch, false);
        swatch.dataset.variantAvailable = 'false';
        if (oosMessage) swatch.setAttribute('data-unavailable-message', oosMessage);
      }
    });
  }

  updateUpsellCTA(target) {
    if (target.getAttribute('name') !== 'upsell-swatch') {
      return;
    }
    let upsellAvailable = target.getAttribute('data-variant-available');
    let upsellCTA = document.querySelector('.cta-button.primary.upsell');
    let pairWithCTA = document.querySelector('.cta-button.primary.pair-with-section');

    if (upsellAvailable && upsellCTA) {
      if (upsellAvailable == 'false') {
        upsellCTA.innerHTML = window.variantStrings.joinTheWaitlist;
      } else if (pairWithCTA) {
        pairWithCTA.innerHTML = window.variantStrings.addProduct;
      } else {
        upsellCTA.innerHTML = window.variantStrings.addToCart;
      }
    }
  }

  updateUpsellDesc(target) {
    const variantDesc = document.querySelector('.upsell-picker.product-form__shade-description');
    let newShadeDesc = target.getAttribute('data-variant-desc');

    if (newShadeDesc && variantDesc) {
      variantDesc.innerHTML = newShadeDesc;
    } else if(!newShadeDesc && variantDesc) {
      variantDesc.innerHTML = '';
    }
  }

  updateUpsellImage(target) {
    const parentContainer = this.closest('.product__upsell');
    const variantImg = parentContainer?.querySelector('.product__upsell-image img');
    let newVariantImgSrc = target.getAttribute('data-variant-image');

    if (variantImg && newVariantImgSrc) {
      variantImg.src = newVariantImgSrc;
      variantImg.srcset = newVariantImgSrc;
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    const optionName = target.name;
    const valueId = target.dataset.optionValueId;

    if (optionName && valueId) {
      this.selectedOptionsMap.set(optionName, valueId);
    }

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const swatchDescriptionValue = target.dataset.variantDesc;
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value] .shade-value-inner');
      const selectedSwatchDescription = target.closest(`.product-form__input`).querySelector('.product-form__shade-description');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      if (selectedSwatchDescription && swatchDescriptionValue) {
        selectedSwatchDescription.innerHTML = swatchDescriptionValue;
      } else if (!swatchDescriptionValue && selectedSwatchDescription) {
        selectedSwatchDescription.innerHTML = '';
      }
    }
  }

  updateRecommendedProductsCTA(target) {
    if (!target.classList.contains('bag-special-swatch-input')) {
      return;
    }
    const variantId = target.dataset.variantId;
    const available = target.dataset.variantAvailable;

    // Try to find button inside the selector container (multi-product version)
    let atb = target.closest('.recommended-selector-container')?.querySelector('.recommended-cta-button');

    // If not found, look for button as sibling after the selector container (single-product version)
    if (!atb) {
      const container = target.closest('.recommended-selector-container');
      if (container) {
        atb = container.parentElement?.querySelector('.recommended-cta-button');
      }
    }

    if (variantId && available && atb) {
      atb.dataset.variantId = variantId;
      atb.disabled = available === 'false';
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  // usable in this component and useful in Swiper initialization callbacks 
  // returns default input
  clickDefaultSelectedShadeInput() {
    const defaultSelectedShadeInput = this.querySelector('.swatch-input__input[data-is-default-selected-shade="true"]');
    if (defaultSelectedShadeInput) {
      defaultSelectedShadeInput.click();
    }
    return defaultSelectedShadeInput;
  }

  // usable in this component and useful in Swiper initialization callbacks 
  goToDefaultSlide() {
    const defaultSelectedShadeInput = this.querySelector('.swatch-input__input[data-is-default-selected-shade="true"]');
    const swiperSlide = defaultSelectedShadeInput?.closest('.swiper-slide');
    if (swiperSlide) {
      const swiperEl = swiperSlide.closest('.swiper-initialized');
      if (swiperEl?.swiper) {
        const index = Array.from(swiperEl.querySelectorAll('.swiper-slide')).indexOf(swiperSlide);
        swiperEl.swiper.slideTo(index, 0);
      }
    }
  }

  goToSelectedSlide() {
    const defaultSelectedShadeInput = this.querySelector('.swatch-input__input:checked');
    const swiperSlide = defaultSelectedShadeInput?.closest('.swiper-slide');
    if (swiperSlide) {
      const swiperEl = swiperSlide.closest('.swiper-initialized');
      if (swiperEl?.swiper) {
        const index = Array.from(swiperEl.querySelectorAll('.swiper-slide')).indexOf(swiperSlide);
        swiperEl.swiper.slideTo(index, 0);
      }
    }
  }

  ensureInputSelected() {
    if (this.selectedOptionsMap) {
      this.selectedOptionsMap.forEach(selectedOption => {
        const input = this.querySelector(`input[type=radio][data-option-value-id="${selectedOption}"]`);
        input.click();
      });
    }
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('fieldset input[type="radio"]:checked')
    ).map(input => input.dataset.optionValueId);
  }

  get selectedVariantInventory() {
    return Array.from(this.querySelectorAll('.non-bundle-input:checked, fieldset input:checked')).map(
      ({ dataset }) => dataset.variantAvailable
    );
  }

  get selectedVariantDiscontinued() {
    return Array.from(this.querySelectorAll('.non-bundle-input:checked, fieldset input:checked')).map(
      ({ dataset }) => dataset.variantDiscontinued
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

function getCountryFromLocale() {
  if (window.Shopify && typeof window.Shopify.country === 'string') {
    return window.Shopify.country.toUpperCase();
  }
  return 'US'; // default fallback
}

function formatCurrencyAmount(amount, country = 'US') {
  const roundedAmount = Math.round(amount * 100) / 100;
  
  if (country === 'US') {
    // US formatting rules
    if (roundedAmount % 1 === 0) {
      // No decimal points if it ends in .00
      return roundedAmount.toString();
    } else {
      // 2 decimal places for US
      return roundedAmount.toFixed(2);
    }
  } else {
    if (roundedAmount % 1 === 0) {
      return roundedAmount.toString();
    } else {
      // Default to 2 decimal places
      return roundedAmount.toFixed(2);
    }
  }
}

function togglePromotionProgressBar(els, show = true, transition = false, delay = '0s') {
  els.forEach(el => {
    el.style.opacity = show ? '1' : '0';
    el.style.display = show ? 'block' : 'none';
  });
}

function handleFreeShippingPromotion(subtotalClean, country) {
  // Update every rendered instance (drawer, cart page, notification) since ids are not unique.
  const shippingEls = document.querySelectorAll('.cart-notification-promotion-free-shipping');
  if (!shippingEls.length) return false;

  const formatRemaining = (remaining) => window.TarteCurrencyFormatter.formatAmount(remaining, {
    country,
    trimTrailingZeros: true,
  });

  let qualified = false;

  shippingEls.forEach((shippingEl) => {
    const threshold = parseFloat(shippingEl.querySelector('.free-shipping-threshold')?.dataset?.freeShippingThreshold || 0);
    const remaining = Math.max(threshold - subtotalClean, 0);
    // A blank/0 threshold means "free shipping, no minimum" -> always qualified (100%).
    const percent = threshold > 0 ? (subtotalClean / threshold) * 100 : 100;

    shippingEl.querySelectorAll('.free-shipping-percentage').forEach(el => el.style.width = `${percent}%`);
    shippingEl.querySelectorAll('.free-shipping-spend-more').forEach(el => el.textContent = formatRemaining(remaining));

    const notQualifiedEls = shippingEl.querySelectorAll('.not-qualified');
    const qualifiedEls = shippingEl.querySelectorAll('.qualified');
    const progressBarEls = shippingEl.querySelectorAll('.free-shipping-progress-bar');

    if (percent < 100) {
      togglePromotionProgressBar(progressBarEls, true);
      notQualifiedEls.forEach(el => { el.style.opacity = '1'; el.style.pointerEvents = ''; });
      qualifiedEls.forEach(el => { el.style.opacity = '0'; el.style.pointerEvents = 'none'; });
    } else {
      shippingEl.style.backgroundColor = "#EAE3E1";
      togglePromotionProgressBar(progressBarEls, false, true);
      notQualifiedEls.forEach(el => { el.style.opacity = '0'; el.style.pointerEvents = 'none'; });
      qualifiedEls.forEach(el => { el.style.opacity = '1'; el.style.pointerEvents = ''; });
      qualified = true;
    }
  });

  return qualified;
}

function handleGWPPromotion(cart, subtotalClean, cartItemCount, freeShippingQualified, country) {
  const gwpEl = document.getElementById('gwp-threshold');
  const gwpSection = document.querySelectorAll('.cart-notification-free-gwp');
  if (!gwpEl || gwpSection.length === 0) return;
    gwpSection.forEach(el => el.style.display = 'flex');

  const freeShippingEl = document.querySelector('.cart-notification-promotion-free-shipping');
  const freeShippingIsActive = !!freeShippingEl;
  const freeShippingIsQualified = !!freeShippingQualified;

  // if free ship enabled, hide gwp progress bar
  const blockGWP = freeShippingIsActive && !freeShippingIsQualified;
  if (blockGWP) {
    gwpSection.forEach(el => {
      el.style.display = 'none';
    });
    return;
  }

  const gwpThreshold = parseFloat(gwpEl.dataset.gwpThreshold || 0);
  const gwpItemThreshold = parseInt(gwpEl.dataset.gwpItemThreshold || '0', 10);

  const useItemCount = gwpItemThreshold > 0;
  const remaining = useItemCount
    ? Math.max(gwpItemThreshold - cartItemCount, 0)
    : Math.max(gwpThreshold - subtotalClean, 0);

  const percent = useItemCount
    ? (cartItemCount / gwpItemThreshold) * 100
    : (subtotalClean / gwpThreshold) * 100;

  // Display GWP section
  gwpSection.forEach(el => el.style.display = 'flex');

  const gwpSpendMoreEls = document.querySelectorAll('.gwp-spend-more');
  const gwpPercentageEls = document.querySelectorAll('.gwp-percentage');
  const gwpQualified = percent >= 100;
  const gwpProgressBar = document.querySelectorAll('.gwp-progress-bar');
  const gwpNotQualified = document.querySelectorAll('.gwp-not-qualified');
  const gwpQualifiedMsg = document.querySelectorAll('.gwp-qualified');
  const gwpWrapper = document.querySelectorAll('.gwp-product-wrapper');

  const formattedRemaining = useItemCount ? remaining.toString() : formatCurrencyAmount(remaining, country);
  gwpSpendMoreEls.forEach(el => el.textContent = formattedRemaining);
  gwpPercentageEls.forEach(el => el.style.width = `${percent}%`);

  const gwpProductId = document.getElementById('gwp-product-id')?.dataset?.gwpProductid;
  const itemInCart = gwpProductId ? cart.items.find(item => item.id == gwpProductId) : false;

  if (!gwpQualified) {
    togglePromotionProgressBar(gwpProgressBar, true);
    togglePromotionProgressBar(gwpNotQualified, true);
    togglePromotionProgressBar(gwpQualifiedMsg, false);
    gwpSection.forEach(el => el.style.justifyContent = 'unset');
  } else if (!itemInCart) {
    gwpWrapper.forEach(el => {
      el.style.opacity = '1';
      el.style.transition = 'opacity 0.2s ease-in-out';
      el.style.transitionDelay = '0s';
    });

    // short-term animation
    setTimeout(() => {
      gwpProgressBar.forEach(el => el.style.display = 'none');
    }, 200);

    const wrapperEl = gwpWrapper[0];
    if (wrapperEl && typeof wrapperEl.style.opacity !== 'undefined') {
      wrapperEl.addEventListener('transitionend', () => {
        gwpSection.forEach(el => el.style.justifyContent = 'center');
        gwpProgressBar.forEach(el => el.style.height = '0');
      }, { once: true });

      wrapperEl.style.opacity = '0';
    }

    togglePromotionProgressBar(gwpProgressBar, false, true);
    togglePromotionProgressBar(gwpNotQualified, false, true);
    togglePromotionProgressBar(gwpQualifiedMsg, true, true);
  } else {
    togglePromotionProgressBar(gwpProgressBar, false);
    togglePromotionProgressBar(gwpNotQualified, false);
    togglePromotionProgressBar(gwpQualifiedMsg, true);
    gwpSection.forEach(el => el.style.justifyContent = 'center');
  }
}

function handleFreeGiftProgressBar(cart, subtotalClean, cartItemCount, country) {
  const bars = document.querySelectorAll('.free-gift-progress-bar__item');
  if (!bars.length) return;

  const qualifyingItems = (cart.items || []).filter(item => {
    const isGwp = item.properties?._tarte_promotion === 'free_gwp';
    const isSample = String(item.properties?._freesample ?? '').toLowerCase() === 'true';
    const isDonation = item.product_type === 'Donation';
    const isGiftCard = item.gift_card === true || item.product_type === 'Gift Card';
    const isRegiosAutoAdded = !!item.properties?._regios_auto_add_to_cart_node_id;
    const isLoyaltyDiscount = !!item.properties?._yotpo_loyalty_discount_type;
    return !isGwp && !isSample && !isDonation && !isGiftCard && !isRegiosAutoAdded && !isLoyaltyDiscount;
  });
  const effectiveSubtotal = qualifyingItems.reduce((sum, item) => sum + item.final_line_price, 0) / 100;
  const effectiveItemCount = qualifyingItems.reduce((sum, item) => sum + item.quantity, 0);

  bars.forEach(bar => {
    bar.style.display = 'flex';

    const thresholdType = bar.dataset.thresholdType;
    const thresholdAmount = parseFloat(bar.dataset.thresholdAmount || 0);
    const thresholdQty = parseInt(bar.dataset.thresholdQty || 0, 10);
    const gwpIds = (bar.dataset.gwpIds || '').split(',').filter(Boolean);
    const gwpMaxCount = parseInt(bar.dataset.gwpMaxCount || 1, 10);
    const isAutoAdd = bar.dataset.promoType === 'auto-add';

    if (isAutoAdd && gwpIds.length > 0) {
      const dismissed = window.getDismissedAutoAddVariants?.() ?? new Set();
      if (gwpIds.some(id => dismissed.has(id))) {
        bar.style.display = 'none';
        return;
      }
    }

    const promotionCode = bar.dataset.promotionCode || '';
    if (promotionCode) {
      const applied = (cart.discount_codes || []).some(
        d => d.code.toLowerCase() === promotionCode.toLowerCase() && d.applicable !== false
      );
      if (!applied) {
        bar.style.display = 'none';
        return;
      }
    }

    let percent = 0;
    let remaining = 0;
    let qualified = false;

    const notQualifiedEl = bar.querySelector('.free-gift-progress-bar__not-qualified');
    const qualifiedPendingEl = bar.querySelector('.free-gift-progress-bar__qualified-pending');
    const qualifiedCompleteEl = bar.querySelector('.free-gift-progress-bar__qualified-complete');
    const percentEl = bar.querySelector('.free-gift-progress-bar__percentage');
    const trackEl = bar.querySelector('.free-gift-progress-bar__track');

    if (thresholdType === 'amount-threshold') {
      remaining = Math.max(thresholdAmount - effectiveSubtotal, 0);
      percent = thresholdAmount > 0 ? (effectiveSubtotal / thresholdAmount) * 100 : 100;
      qualified = percent >= 100;
      if (notQualifiedEl && !qualified) {
        const template = bar.dataset.notQualifiedAmount || '';
        const formattedAmount = window.TarteCurrencyFormatter.formatAmount(remaining, { country, trimTrailingZeros: true });
        notQualifiedEl.innerHTML = `<span>${template.replace('{amount}', ` ${formattedAmount} `)}</span>`;
      }
    } else if (thresholdType === 'quantity-threshold') {
      remaining = Math.max(thresholdQty - effectiveItemCount, 0);
      percent = thresholdQty > 0 ? (effectiveItemCount / thresholdQty) * 100 : 100;
      qualified = percent >= 100;
      if (notQualifiedEl && !qualified) {
        const templateKey = remaining === 1 ? 'notQualifiedQtyOne' : 'notQualifiedQtyOther';
        const template = bar.dataset[templateKey] || '';
        notQualifiedEl.innerHTML = `<span>${template.replace('{count}', ` <strong>${remaining}</strong> `)}</span>`;
      }
    }

    if (percentEl) percentEl.style.width = `${Math.min(percent, 100)}%`;

    if (qualified) {
      bar.style.backgroundColor = "#ead0ff";

      const gwpInCart = gwpIds.length > 0
        ? cart.items.filter(item =>
            item.properties?._tarte_promotion === 'free_gwp' &&
            (gwpIds.includes(String(item.product_id)) || gwpIds.includes(String(item.variant_id)))
          ).length
        : gwpMaxCount;
      const allGwpAdded = gwpInCart >= gwpMaxCount;

      togglePromotionProgressBar(trackEl ? [trackEl] : [], false, true);
      if (notQualifiedEl) { notQualifiedEl.style.opacity = '0'; notQualifiedEl.style.pointerEvents = 'none'; }
      if (isAutoAdd) {
        if (allGwpAdded) {
          if (qualifiedPendingEl) { qualifiedPendingEl.style.opacity = '0'; qualifiedPendingEl.style.pointerEvents = 'none'; }
          if (qualifiedCompleteEl) { qualifiedCompleteEl.style.opacity = '1'; qualifiedCompleteEl.style.pointerEvents = ''; }
        } else {
          if (qualifiedPendingEl) { qualifiedPendingEl.style.opacity = '1'; qualifiedPendingEl.style.pointerEvents = ''; }
          if (qualifiedCompleteEl) { qualifiedCompleteEl.style.opacity = '0'; qualifiedCompleteEl.style.pointerEvents = 'none'; }
        }
      } else {
        if (allGwpAdded) {
          if (qualifiedPendingEl) { qualifiedPendingEl.style.opacity = '0'; qualifiedPendingEl.style.pointerEvents = 'none'; }
          if (qualifiedCompleteEl) { qualifiedCompleteEl.style.opacity = '1'; qualifiedCompleteEl.style.pointerEvents = ''; }
        } else {
          if (qualifiedPendingEl) { qualifiedPendingEl.style.opacity = '1'; qualifiedPendingEl.style.pointerEvents = ''; }
          if (qualifiedCompleteEl) { qualifiedCompleteEl.style.opacity = '0'; qualifiedCompleteEl.style.pointerEvents = 'none'; }
        }
      }
    } else {
      togglePromotionProgressBar(trackEl ? [trackEl] : [], true);
      if (notQualifiedEl) { notQualifiedEl.style.opacity = '1'; notQualifiedEl.style.pointerEvents = ''; }
      if (qualifiedPendingEl) { qualifiedPendingEl.style.opacity = '0'; qualifiedPendingEl.style.pointerEvents = 'none'; }
      if (qualifiedCompleteEl) { qualifiedCompleteEl.style.opacity = '0'; qualifiedCompleteEl.style.pointerEvents = 'none'; }
    }
  });
}

function getNonSampleItemCount(cart) {
  return cart.items.reduce((sum, item) => {
    const isSample = String(item.properties?._freesample ?? '').toLowerCase() === 'true';
    const isDonation = item.product_type === 'Donation';
    const isGiftCard = item.gift_card === true || item.product_type === 'Gift Card';
    return isSample || isDonation || isGiftCard ? sum : sum + Number(item.quantity || 0);
  }, 0);
}

function isFullCartDataForPromotions(cartData) {
  return (
    cartData &&
    Array.isArray(cartData.items) &&
    typeof cartData.item_count === 'number' &&
    typeof cartData.total_price === 'number'
  );
}

function getFullCartDataForPromotionsFromPayload(payload) {
  const cartData = payload?.detail?.cartData || payload?.cartData || payload;
  return isFullCartDataForPromotions(cartData) ? cartData : null;
}

async function getCartDataForPromotions(payload, country) {
  const payloadCart = getFullCartDataForPromotionsFromPayload(payload);
  if (payloadCart) return payloadCart;

  // Read authoritative cart state, not the localStorage cache which lags a cart mutation by one op (TARTE-18078).
  const res = await fetch(`/cart.js?country=${country}`, { cache: 'no-store' });
  return res.json();
}

// Latest-wins token: a newer call cancels older in-flight ones so only the freshest cart total paints.
let _promoLatestToken = 0;
let _promoDebounceTimer = null;

async function runCalculatePromotions(eventData, token) {
  const country = getCountryFromLocale();

  try {
    const cart = await getCartDataForPromotions(eventData, country);
    // A newer invocation superseded this one; drop it so we don't paint stale data.
    if (token !== _promoLatestToken) return;

    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0 || cart.total_price === 0) {
      document.querySelectorAll('.cart-notification-promotion').forEach(el => {
        el.style.display = 'none';
      });
      return;
    }

    const subtotalCents = cart.total_price;
    const giftCardTotalCents = cart.items.reduce((total, item) => item.gift_card === true ? total + (item.final_line_price || 0) : total, 0);
    const donationTotalCents = cart.items.reduce((total, item) => item.product_type === 'Donation' ? total + (item.final_line_price || 0) : total, 0);
    const subtotalClean = (subtotalCents - giftCardTotalCents - donationTotalCents) / 100;
    const cartItemCount = getNonSampleItemCount(cart);

    document.querySelectorAll('.cart-notification-promotion').forEach(el => { el.style.display = 'block'; });
    document.querySelectorAll('.cart-total').forEach(el => { el.setAttribute('cartTotal', subtotalClean); });

    const freeShippingQualified = handleFreeShippingPromotion(subtotalClean, country);
    handleGWPPromotion(cart, subtotalClean, cartItemCount, freeShippingQualified, country);
    handleFreeGiftProgressBar(cart, subtotalClean, cartItemCount, country);
    window.updateSampleButtons?.(cart);

  } catch (error) {
    console.error("Error fetching cart or processing promotions:", error);
  }
}

// debounce the burst of triggers per cart action into a single run (at most one /cart.js read).
window.calculatePromotions = function (eventData) {
  const token = ++_promoLatestToken;
  if (_promoDebounceTimer) { clearTimeout(_promoDebounceTimer); _promoDebounceTimer = null; }

  // When the event already carries full cart data no fetch is needed, so run immediately
  // rather than waiting out the debounce — this closes the blank window after HTML re-renders.
  const payloadCart = getFullCartDataForPromotionsFromPayload(eventData);
  if (payloadCart && Array.isArray(payloadCart.items) && payloadCart.total_price >= 0) {
    runCalculatePromotions(eventData, token);
  } else {
    // No cart data in payload (e.g. cartdrawer:open) — debounce to coalesce rapid events before fetching.
    _promoDebounceTimer = setTimeout(() => {
      _promoDebounceTimer = null;
      runCalculatePromotions(eventData, token);
    }, 75);
  }
};

document.addEventListener('cartdrawer:open', window.calculatePromotions);
document.addEventListener('tarte:cart-data-updated', window.calculatePromotions);

window.fetchGraphQLData = async (query, variables) => {
  try {
    const response = await fetch(`https://${Shopify.shop}/api/2025-04/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_API_KEY,
      },
      body: JSON.stringify({ query, variables }),
    });
    const { data, errors } = await response.json();
    if (errors) throw new Error(errors.map(e => e.message).join(', '));
    return data;
  } catch (e) {
    console.error('Error fetching data:', e);
    return null;
  }
};

// add props to products with promo messaging tags
window.getPromoLineItemProperties = (selectors, { mode = 'labels' } = {}) => {
  if (typeof selectors === 'string') selectors = [selectors];

  let attr;
  for (const selector of selectors) {
    attr = document.querySelector(selector);
    if (attr) break;
  }
  if (!attr) return {};

  const excluded  = attr.dataset.excludedFromPromotions === 'true';
  const finalSale = attr.dataset.finalSale === 'true';

  const props = {};
  if (mode === 'labels') {
    if (excluded)  props[window.promoMessaging.exclusionsApply] = window.promoMessaging.noPromos;
    if (finalSale) props[window.promoMessaging.finalSale]       = window.promoMessaging.noExchangesOrReturns;
  } else if (mode === 'flags') {
    if (excluded)  props._promo_exclusions = 'true';
    if (finalSale) props._promo_final_sale = 'true';
  }
  return props;
};

window.getBagSpecialSavings = function(productContainer) {
  try {
    const productJson = productContainer?.querySelector('.bag-special-product-json');
    if (!productJson) return null;

    const data = JSON.parse(productJson.textContent);
    const compareAtPrice = data.compare_at_price || data.price;
    const discountPrice = data.discount_price;

    if (compareAtPrice && discountPrice && compareAtPrice > discountPrice) {
      return compareAtPrice - discountPrice;
    }
    return null;
  } catch (e) {
    console.error('Error calculating bag special savings:', e);
    return null;
  }
};

/** Queue to track pending bag special savings to be applied */
window.pendingBagSpecialSavings = window.pendingBagSpecialSavings || [];

window.queueBagSpecialSavings = function(variantId, savings) {
  if (!savings || savings <= 0) return;
  window.pendingBagSpecialSavings.push({ variantId, savings });
};

window.applyPendingBagSpecialSavings = async function() {
  if (!window.pendingBagSpecialSavings.length) return;

  try {
    const cartData = window.getTarteCartData();
    if (!cartData?.items) return;

    let updated = false;

    // Process all pending savings
    window.pendingBagSpecialSavings.forEach(({ variantId, savings }) => {
      const item = cartData.items.find(item => item.variant_id === variantId);
      if (item && !item.bag_special_savings) {
        item.bag_special_savings = savings;
        updated = true;
      }
    });

    if (updated) {
      window.setTarteCartData(cartData);
      if (typeof window.updateCustomKitSavingsBanner === 'function') {
        await window.updateCustomKitSavingsBanner();
      }
    }

    // Clear the queue
    window.pendingBagSpecialSavings = [];
  } catch (e) {
    console.error('Error applying pending bag special savings:', e);
  }
};

window.addToCartBySku = async (sku, context = null, fromKit = false, bagSpecialSavings = null) => {
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';

  const responseSections = [
    'cart-icon-bubble',
    'cart-drawer',
  ];

  // Get properties based on context
  let promoSelectors;
  if (context === 'sticky') {
    promoSelectors = ['.sticky-bar-atb'];
  } else if (context === 'pair-with') {
    promoSelectors = ['.product__upsell variant-selects'];
  } else {
    promoSelectors = [
      '#Quickshop-Modal product-info[data-view-context="Quickview"]',
      'cart-drawer.active #mini-cart-recommendations .recommended-product'
    ];
  }
  const promoProperties = {
    ...window.getPromoLineItemProperties(promoSelectors),
    ...window.getPromoLineItemProperties(promoSelectors, { mode: 'flags' }),
  };

  const contextProperties = {
    _source: context ?? 'unknown',
    ...(context === 'tarte_gwp' && { _tarte_promotion: 'free_gwp', _tarte_gwp_type: 'user_selected' }),
  };

  // Add bag special savings if provided
  const bagSpecialProperties = {};
  if (bagSpecialSavings !== null && typeof bagSpecialSavings === 'number' && bagSpecialSavings > 0) {
    bagSpecialProperties._bag_special_savings = bagSpecialSavings;
  }

  const properties = {
    ...promoProperties,
    ...contextProperties,
    ...bagSpecialProperties,
  };

  config.body = JSON.stringify({
    id: sku,
    properties,
    sections: responseSections.join(',')
  });

  const variantIdForHighlight = context === 'recommended' ? String(sku) : null;
  if (variantIdForHighlight) {
    window.pendingBagSpecialCartVariant = variantIdForHighlight;
  }

  try {
    const fetchResponse = await fetch(`${window.routes.cart_add_url}`, config);
    const response = await fetchResponse.json();

    if (response.status) {
      const messageName = window.PUB_SUB_EVENTS ? window.PUB_SUB_EVENTS.cartError : 'cart-error';
      window.publish(messageName, {
        source: 'product-form',
        productVariantId: sku,
        errors: response.errors || response.description,
        message: response.message,
      });
    }

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    if (response.sections) {
      if (variantIdForHighlight && response.sections['cart-drawer']) {
        response.sections['cart-drawer'] = applyPendingBagSpecialState(
          response.sections['cart-drawer'],
          variantIdForHighlight
        );
      }
      cart.renderContents(response, fromKit);
    } else {
      const sections = await fetchCartSections(['cart-drawer', 'cart-icon-bubble']);
      if (variantIdForHighlight && sections['cart-drawer']) {
        sections['cart-drawer'] = applyPendingBagSpecialState(
          sections['cart-drawer'],
          variantIdForHighlight
        );
      }
      response.sections = sections;
      cart.renderContents(response, fromKit);
    }

    if (variantIdForHighlight) {
      highlightBagSpecialCartItem(variantIdForHighlight);
    }

    // For bag specials (recommended items), manually trigger cart data update - ensures localStorage is synced and pending savings are applied
    if (context === 'recommended' && typeof window.updateTarteCartData === 'function') {
      window.updateTarteCartData({ source: 'bag-special', cartData: response });
    }

    window.updateSampleButtons(response);
  } catch (e) {
    console.error(e);
  }
}

 function fetchCartSections(sections = ['cart-drawer', 'cart-icon-bubble']) {
    const root = window.Shopify.routes?.root || '';
    const localePrefix = root.endsWith('/') ? root.slice(0, -1) : root;
    const params = new URLSearchParams();
    params.append('sections', sections.join(','));
    return fetch(`${localePrefix}/?${params.toString()}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(res => {
      if (!res.ok) throw new Error(`Sections request failed: ${res.status}`);
      return res.json();
    });
}

window.toShopifyVariantGid = function(variantId) {
  const value = String(variantId || '').trim();
  if (!value) return '';
  return value.startsWith('gid://shopify/ProductVariant/')
    ? value
    : `gid://shopify/ProductVariant/${value}`;
};

window.getSelectedBundleComponents = function(scope = document) {
  const bundleContainers = scope.querySelectorAll('.bundle-variant-selects');
  const components = [];

  bundleContainers.forEach((container, index) => {
    const checked = Array.from(container.querySelectorAll('input.bundle-swatch:checked'));
    if (!checked.length) return;

    let variantId = null;

    if (checked.length === 1) {
      variantId = checked[0].getAttribute('bundle-variant-id') || checked[0].dataset.variantId;
    } else {
      const variantsScript = container.querySelector('script[data-bundle-variants]');
      if (!variantsScript) return;

      const variants = JSON.parse(variantsScript.textContent);
      const selectedOptions = checked.map(input => ({
        position: parseInt(input.dataset.optionIndex) + 1,
        value: input.dataset.optionValue
      }));

      const matched = variants.find(v =>
        selectedOptions.every(({ position, value }) => v[`option${position}`] === value)
      );
      if (matched) variantId = String(matched.id);
    }

    const merchandiseId = window.toShopifyVariantGid(variantId);
    if (!merchandiseId) return;

    components.push({
      merchandiseId,
      variantId: String(variantId),
      quantity: 1,
      slot: String(container.dataset.bundleSlot || index + 1),
      productHandle: container.dataset.productHandle || ''
    });
  });

  return components;
};

window.buildBundleLineItem = function({ parentVariantId, properties, components }) {
  const parentId = String(parentVariantId || '').trim();
  const bundleProperties = { ...properties };

  bundleProperties._bundle_attributes = JSON.stringify({ ...properties });
  bundleProperties._bundle_components = JSON.stringify(components || []);

  return {
    id: parentId,
    quantity: 1,
    properties: bundleProperties
  };
};

window.addToCartBundle = async (cta, selector) => {
  cta.classList.add('loading');
  const cartElem = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const productInfoWrapper =
    document.querySelector(`${selector} .product__info-wrapper`) ||
    document.querySelector(selector) ||
    document;
  const bundleKey = `bundle_${new Date().getTime()}`;
  const bundleProductID = document.querySelector(`${selector} input[name="bundle_product_id"]`)?.value ||
    document.querySelector(`${selector} product-info`)?.getAttribute('data-product-id'); // 'Bundle Product ID';
  const bundleName = document.querySelector(`${selector} input[name="bundle_product_name"]`)?.value;
  const bundleProductHandles = document.querySelector(`${selector} input[name="bundle_product_handles"]`)?.value;
  
  const bundleImage = document.querySelector(`${selector} input[name="bundle_image"]`)?.value; // 'Bundle Image URL';
  const bundleShade = document.querySelector(`${selector} input[name="bundle_shade"]`)?.value;
  const bundleSize = document.querySelector(`${selector} input[name="bundle_size"]`)?.value;
  
  const bundleHandle = document.querySelector(`${selector} input[name="bundle_handle"]`)?.value; // 'Bundle Handle';
  const parentVariant = document.querySelector(`${selector} .product-variant-id`)?.value;
  const bundleSku = document.querySelector(`${selector} input[name="bundle_sku"]`)?.value;
  const form = document.querySelector(`${selector} form`);

  const promoFlags = window.getPromoLineItemProperties(
    ['#Quickshop-Modal product-info[data-view-context="Quickview"]'],
    { mode: 'flags' }
  );
  
  try {
    if (typeof window.updateDuplicateBundleSwatchAvailability === 'function') {
      await window.updateDuplicateBundleSwatchAvailability(productInfoWrapper, { forceFetch: true });
    }

    if (
      window.hasUnavailableSelectedBundleInput?.(productInfoWrapper) ||
      cta.textContent.trim() === window.variantStrings.joinTheWaitlist
    ) {
      document.getElementById('Waitlist-Modal-product')?.show();
      cta.removeAttribute('aria-disabled');
      return;
    }

    const bundleComponents = window.getSelectedBundleComponents(productInfoWrapper);
    if (!bundleComponents.length || !parentVariant) {
      throw new Error('No bundle selections found');
    }

    const bundleData = {
      data: {
        items: [window.buildBundleLineItem({
            parentVariantId: parentVariant,
            components: bundleComponents,
            properties: {
              _bundle_key: bundleKey,
              _bundle_name: bundleName,
              _bundle_product_handles: bundleProductHandles,
              _bundle_image: bundleImage,
              _bundle_product_id: bundleProductID,
              _bundle_handle: bundleHandle,
              _bundle_shade: bundleShade,
              _bundle_size: bundleSize,
              _parent_id: parentVariant,
              _bundle_sku: bundleSku,
              ...promoFlags
            }
          })],
        sections: cartElem
          ? cartElem.getSectionsToRender().map(s => s.id)
          : []
      }
    };

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bundleData.data)
    });
    const payload = await response.json();
    if (payload.status) {
      form.handleErrorMessage(payload.description);
      throw payload;
    }
  
    // Publish cart update event for localStorage sync and other listeners
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'add-to-cart-bundle',
      cartData: payload,
    });

    if (payload.sections) {
        cartElem.renderContents(payload);
    } else {
      // Fallback: fetch rendered cart sections.
      fetchCartSections(['cart-drawer', 'cart-icon-bubble'])
        .then(sections => {
          // add the sections to the response
          payload.sections = sections;
          cartElem.renderContents(payload);
        });
    }

  } catch (err) {
    console.error('Bundle add to cart failed:', err);
    alert('Something went wrong. Please try reloading and adding to cart again.');
    // prevent bundle from being added
    return;
  } finally {
    cta.classList.remove('loading');
    if (!this.error) cta.removeAttribute('aria-disabled');
    if (cartElem?.classList.contains('is-empty')) cartElem.classList.remove('is-empty');
  }
};

function getLocaleConfigs(withFallback = false) {
  const tag = document.getElementById('locale-configs');
  let localeConfigs = [];
  if (tag) {
    try {
      const parsed = JSON.parse(tag.textContent.trim());
      localeConfigs = Array.isArray(parsed?.localeConfigs) ? parsed.localeConfigs : [];
    } catch (e) {
      console.error('Error parsing locale-configs JSON', e);
    }
  }
  if (withFallback && (!Array.isArray(localeConfigs) || localeConfigs.length === 0)) {
    console.warn('No localeConfigs found. Falling back to default US/en-US.');
    return [{ country: 'US', language: 'en-US' }];
  }
  return localeConfigs || [];
}

function makePriceQuery({countryEnum, languageEnum, mode}) {
  if ( mode === 'bundle') {
    return `
      query fetchCompanionProducts($ids: [ID!]!) 
      @inContext(country: ${countryEnum}, language: ${languageEnum}) {
        nodes(ids: $ids) {
          ... on Product {
            priceRange {
              maxVariantPrice { amount }
            }
          }
        }
      }
    `;
  }

  return `
    query fetchProductPrice($id: ID!)
    @inContext(country: ${countryEnum}, language: ${languageEnum}) {
      node(id: $id) {
        __typename
        ... on Product {
          priceRange {
            minVariantPrice { amount }
            maxVariantPrice { amount }
          }
        }
        ... on ProductVariant {
          product {
            priceRange {
              minVariantPrice { amount }
              maxVariantPrice { amount }
            }
          }
        }
      }
    }
  `;
}

function getLocaleConfigForCountry(country) {
  const countryCode = String(country || 'US').toUpperCase();
  const localeConfig = getLocaleConfigs(true).find(({ country: localeCountry }) =>
    String(localeCountry || '').toUpperCase() === countryCode
  );

  return {
    country: countryCode,
    language: localeConfig?.language || 'en-US'
  };
}

function normalizeStorefrontLanguageEnum(language) {
  const normalized = String(language || 'en-US').trim().replace(/-/g, '_').toUpperCase();
  const regionalLanguageCodes = ['PT_BR', 'PT_PT', 'ZH_CN', 'ZH_TW'];

  if (regionalLanguageCodes.includes(normalized)) return normalized;

  return normalized.split('_')[0] || 'EN';
}

function makeVariantPriceQuery({ countryEnum, languageEnum }) {
  return `
    query fetchVariantPrice($id: ID!)
    @inContext(country: ${countryEnum}, language: ${languageEnum}) {
      node(id: $id) {
        ... on ProductVariant {
          price { amount }
        }
      }
    }
  `;
}

function makeVariantPricesQuery() {
  return `
    query fetchVariantPrices($ids: [ID!]!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      nodes(ids: $ids) {
        __typename
        ... on ProductVariant {
          id
          price {
            amount
            currencyCode
          }
        }
      }
    }
  `;
}

function getVariantPriceCacheKey(variantGid, countryEnum, languageEnum) {
  return `${variantGid}:${countryEnum}:${languageEnum}`;
}

window.customKitVariantPrices = window.customKitVariantPrices || window.customKitParentVariantPrices || {};
window.customKitParentVariantPrices = window.customKitVariantPrices;

window.fetchVariantPricesForCountry = async function(variantIds, country) {
  const { country: countryEnum, language } = getLocaleConfigForCountry(country);
  const languageEnum = normalizeStorefrontLanguageEnum(language);
  const pricesByVariantGid = {};
  const variantGids = Array.from(new Set(
    (Array.isArray(variantIds) ? variantIds : [variantIds])
      .map(variantId => window.toShopifyVariantGid(variantId))
      .filter(Boolean)
  ));

  const idsToFetch = variantGids.filter(variantGid => {
    const cacheKey = getVariantPriceCacheKey(variantGid, countryEnum, languageEnum);
    if (Object.prototype.hasOwnProperty.call(window.customKitVariantPrices, cacheKey)) {
      pricesByVariantGid[variantGid] = window.customKitVariantPrices[cacheKey];
      return false;
    }

    return true;
  });

  if (!idsToFetch.length) return pricesByVariantGid;

  try {
    const chunks = [];
    for (let i = 0; i < idsToFetch.length; i += 250) {
      chunks.push(idsToFetch.slice(i, i + 250));
    }

    for (const ids of chunks) {
      const data = await window.fetchGraphQLData(
        makeVariantPricesQuery(),
        {
          ids,
          country: countryEnum,
          language: languageEnum,
        }
      );

      (data?.nodes || []).forEach(node => {
        if (node?.__typename !== 'ProductVariant') return;

        const amount = Number(node.price?.amount);
        if (!Number.isFinite(amount) || amount < 0) return;

        const cacheKey = getVariantPriceCacheKey(node.id, countryEnum, languageEnum);
        window.customKitVariantPrices[cacheKey] = amount;
        pricesByVariantGid[node.id] = amount;
      });
    }
  } catch (err) {
    console.warn(`[CK Pricing] Failed to fetch batched variant prices for ${countryEnum}:`, err);
  }

  return pricesByVariantGid;
};

window.fetchVariantPriceForCountry = async function(variantId, country, options = {}) {
  const variantGid = window.toShopifyVariantGid(variantId);
  const { country: countryEnum, language } = getLocaleConfigForCountry(country);
  const languageEnum = normalizeStorefrontLanguageEnum(language);
  const cacheKey = getVariantPriceCacheKey(variantGid, countryEnum, languageEnum);

  if (!variantGid) return null;
  if (Object.prototype.hasOwnProperty.call(window.customKitVariantPrices, cacheKey)) {
    const cachedAmount = Number(window.customKitVariantPrices[cacheKey]);
    return Number.isFinite(cachedAmount) && cachedAmount > 0 ? cachedAmount : null;
  }

  if (options.useBatch !== false && typeof window.fetchVariantPricesForCountry === 'function') {
    const pricesByVariantGid = await window.fetchVariantPricesForCountry([variantGid], countryEnum);
    const amount = Number(pricesByVariantGid?.[variantGid]);

    if (Number.isFinite(amount) && amount > 0) {
      return amount;
    }
  }

  try {
    const data = await window.fetchGraphQLData(
      makeVariantPriceQuery({ countryEnum, languageEnum }),
      { id: variantGid }
    );
    const amount = parseFloat(data?.node?.price?.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error('No variant price found');
    }

    window.customKitVariantPrices[cacheKey] = amount;
    return amount;
  } catch (err) {
    console.warn(`[CK Savings] Failed to fetch parent variant price for ${countryEnum}:`, err);
    return null;
  }
};

window.fetchPrices = async ({productId, which = 'min', mode, allowFallbackLocales}) => {
  const isProduct = mode === 'product';
  const localeConfigs = getLocaleConfigs(allowFallbackLocales);
  const selector = which === 'max' ? 'maxVariantPrice' : 'minVariantPrice';
  const fetchedPrices = {};

  const promises = localeConfigs.map(async ({ country, language }) => {
    const countryEnum  = country.toUpperCase();
    const languageEnum = language.split('-')[0].toUpperCase();
    const query = makePriceQuery({countryEnum, languageEnum, mode});
    const productLookup = `gid://shopify/Product/${productId}`;
    const variantLookup = `gid://shopify/ProductVariant/${productId}`;

    if (isProduct) {
      try {
      const data = await window.fetchGraphQLData(query, { id: productLookup});
      const priceData = data?.node?.priceRange?.[selector];

      if (!priceData) throw new Error('No price data found');

      const amt = parseFloat(priceData.amount).toFixed(2);
      fetchedPrices[`_price-${countryEnum}`] = amt;
    } catch (err) {
      try  {
        const data = await window.fetchGraphQLData(query, { id: variantLookup });
        const priceData = data?.node?.product?.priceRange?.[selector];

        if (!priceData) throw new Error('No price data found');

        const amt = parseFloat(priceData.amount).toFixed(2);
        fetchedPrices[`_price-${countryEnum}`] = amt;
      } catch (err) {
        console.warn(`Failed to fetch product price for ${countryEnum}:`, err);
      }
    }
    } else {
      try {
        const data = await window.fetchGraphQLData(query, { ids: productLookup });
        const amt  = parseFloat(data.nodes[0].priceRange.maxVariantPrice.amount).toFixed(2);
        fetchedPrices[`_bundle_price-${countryEnum}`] = amt;
      } catch (err) {
        console.warn(`Failed to fetch price for ${countryEnum}:`, err);
      }
    }
  });

  await Promise.all(promises);
  if (Object.keys(fetchedPrices).length === 0) {
    throw new Error('No prices retrieved');
  }
  return fetchedPrices;
}

window.fetchProductPrices = function(productId, { which = 'min' } = {}) {
  return fetchPrices({
    mode: 'product',
    productId,
    which,
    allowFallbackLocales: true
  });
};

window.bundlePricesByProductId = window.bundlePricesByProductId || {};
window.bundlePricesProductId = window.bundlePricesProductId || null;

window.fetchBundlePrices = async function(productId, { which = 'max' } = {}) {
  const productIdKey = String(productId || '');

  // reset window.bundlePrices. In case it fails, it should not have previous value
  window.bundlePrices = null;
  window.bundlePricesProductId = null;

  const prices = await fetchPrices({
    mode: 'bundle',
    which,
    productId,
    allowFallbackLocales: false
  });

  if (productIdKey) {
    window.bundlePricesByProductId[productIdKey] = prices;
    window.bundlePricesProductId = productIdKey;
  }
  window.bundlePrices = prices;

  return prices;
};

window.getBundlePrices = async function(productId, options = {}) {
  const productIdKey = String(productId || '');

  if (
    productIdKey &&
    window.bundlePricesByProductId?.[productIdKey]
  ) {
    window.bundlePrices = window.bundlePricesByProductId[productIdKey];
    window.bundlePricesProductId = productIdKey;
    return window.bundlePrices;
  }

  return window.fetchBundlePrices(productId, options);
};

// const DETAILS_ID = 'Details-FreeSamples';
const DETAILS_IDS = [
  'Details-FreeSamples',
  'Details-BagSpecialsRecommendations',
];

function preserveAccordion(fn) {
  const openIds = DETAILS_IDS
    .map(id => {
      const el = document.getElementById(id);
      return el && el.open ? id : null;
    })
    .filter(Boolean);

  // Capture open state for dynamic .free-gift elements by discount key
  const openFreeGiftKeys = [...document.querySelectorAll('.free-gift[data-discount-key]')]
    .filter(el => el.open)
    .map(el => el.dataset.discountKey);

  return Promise.resolve(fn()).then(json => {
    openIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.open = true;
    });
    openFreeGiftKeys.forEach(key => {
      const el = document.querySelector(`.free-gift[data-discount-key="${CSS.escape(key)}"]`);
      if (el) el.open = true;
    });
    return json;
  });
}

 window.updateSampleButtons = function(cartData) {
  const incomingCart = cartData?.detail?.cartData || cartData?.cartData || cartData;
  const cachedCart = Array.isArray(incomingCart?.items) ? incomingCart : window.getTarteCartData?.();
  const updateButtons = (cart) => {
      if (!Array.isArray(cart?.items)) return cart;

      const sampleItems = cart.items.filter(item =>
        item.properties['_freesample'] === 'true'
      );
      const selectedCount = sampleItems.length;
      const wrapper = document.querySelector('.general-samples-swiper .swiper-wrapper');
      const limit = wrapper
        ? parseInt(wrapper.dataset.maxLimit, 10)
        : 0;

      document.querySelectorAll('.sample-select-button').forEach(btn => {
        const sku = Number(btn.dataset.variantId);
        const inCart = sampleItems.some(item => item.variant_id === sku);

        if (inCart) {
          btn.textContent = 'Remove';
          btn.classList.add('selected');
          btn.disabled = false;
        } else if (selectedCount >= limit) {
          btn.textContent = 'Select';
          btn.classList.remove('selected');
          btn.disabled = true;
        } else {
          btn.textContent = 'Select';
          btn.classList.remove('selected');
          btn.disabled = false;
        }
      });
      return cart;
    };

  if (Array.isArray(cachedCart?.items)) {
    return Promise.resolve(updateButtons(cachedCart));
  }

  return fetch(window.routes.cart_url + '.js')
    .then(r => r.json())
    .then(updateButtons);
}

document.addEventListener('DOMContentLoaded', window.updateSampleButtons);

window.selectFreeSample = async function (newSku) {
  const buttons = document.querySelectorAll('.sample-select-button');
  let updatedCart;
  buttons.forEach(btn => {
    btn.classList.add('is-processing');
    btn.disabled = true;
  });

  try {
    const cart = await fetch(window.routes.cart_url + '.js').then(res => res.json());
    const currentSample = cart.items.find(item =>
      item.properties?.['_freesample'] === 'true'
    );

    if (currentSample && currentSample.variant_id !== Number(newSku)) {
      await window.removeFreeSample(currentSample.variant_id);
      await waitForSampleRemoval(currentSample.variant_id);
    }

    updatedCart = await window.addToCartFreeSample(newSku);
  } catch (err) {
    console.error('Error swapping sample:', err);
  } finally {
    buttons.forEach(btn => btn.classList.remove('is-processing'));
    window.updateSampleButtons(updatedCart);
  }
};

// Wait until the sample is actually removed from the cart
async function waitForSampleRemoval(sku, maxAttempts = 5) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const cart = await fetch(window.routes.cart_url + '.js').then(res => res.json());
    const stillThere = cart.items.find(item =>
      item.properties?.['_freesample'] === 'true' && item.variant_id === Number(sku)
    );

    if (!stillThere) return;
    await new Promise(res => setTimeout(res, 300));
  }
}

window.addToCartFreeSample = async function(sku) {
  return preserveAccordion(async () => {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      id: sku,
      quantity: 1,
      properties: { _freesample: 'true' },
      sections: 'cart-icon-bubble,cart-drawer'
    });
    const country = getCountryFromLocale();
    const url = `${window.routes.cart_add_url}?country=${encodeURIComponent(country)}`;
    const json = await fetch(url, config).then(r => r.json());
    const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    cartEl.renderContents(json);
    window.initFreeSamplesSwiper(document.querySelector('.general-samples-swiper.swiper'));
    window.updateSampleButtons(json);

    // Publish cart update event for localStorage sync and other listeners
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'add-free-sample',
      cartData: json,
    });

    return json;
  });
};

window.removeFreeSample = async function(sku) {
  return preserveAccordion(async () => {
    // find the line index
    const cart = await fetch(window.routes.cart_url + '.js').then(r => r.json());
    const idx = cart.items.findIndex(item =>
      item.variant_id === Number(sku) &&
      item.properties['_freesample'] === 'true'
    );
    if (idx === -1) return;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      line: idx + 1,
      quantity: 0,
      sections: 'cart-icon-bubble,cart-drawer'
    });
    const country = getCountryFromLocale();
    const url = `${window.routes.cart_change_url}?country=${encodeURIComponent(country)}`;
    const json = await fetch(url, config).then(r => r.json());
    const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    cartEl.renderContents(json);
    window.initFreeSamplesSwiper(document.querySelector('.general-samples-swiper.swiper'));
    window.updateSampleButtons();

    // Publish cart update event for localStorage sync and other listeners
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'remove-free-sample',
      cartData: json,
    });

    return json;
  });
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest('.sample-select-button');
  if (!btn) return;

  e.preventDefault();
  const sku = btn.dataset.variantId;

  if (btn.classList.contains('selected')) {
    window.removeFreeSample(sku).then((cart) => {
      window.updateSampleButtons(cart);
    });
  } else {
    window.selectFreeSample(sku);
  }

});

window.bagSpecialQuickviewState = null;
window.pendingBagSpecialCartVariant = window.pendingBagSpecialCartVariant || null;

function getCustomKitQuickviewController() {
  if (window.customKitQuickviewController?.openCustomKitQuickShop) {
    return window.customKitQuickviewController;
  }

  const existing = document.querySelector('custom-kit');
  if (existing?.openCustomKitQuickShop) {
    window.customKitQuickviewController = existing;
    return existing;
  }

  if (typeof customElements?.get !== 'function' || !customElements.get('custom-kit')) {
    return null;
  }

  try {
    const helper = document.createElement('custom-kit');
    window.customKitQuickviewController = helper;
    return helper;
  } catch (error) {
    console.warn('Unable to create custom kit quickview controller', error);
    return null;
  }
}

function normalizeKitQuickviewProduct(options = {}) {
  if (!options || typeof options !== 'object') return null;
  const source = options.productData || options.product || {};
  const payload = { ...source };

  const derivedUrl =
    options.url ||
    payload.url ||
    (payload.handle ? `/products/${payload.handle}` : null) ||
    (options.handle ? `/products/${options.handle}` : null);

  if (!derivedUrl) return null;

  payload.url = derivedUrl;
  if (!Array.isArray(payload.variants)) {
    payload.variants = [];
  }

  return payload;
}

function inferQuickviewSource(productOptions = {}) {
  // Explicit source wins
  if (productOptions?.source) return productOptions.source;

  // Bag specials flag the state
  if (window.bagSpecialQuickviewState?.source) return window.bagSpecialQuickviewState.source;

  // Fallback to PDP
  return 'pdp';
}

async function hydrateQuickviewFromPdp(product = {}) {
  const modal = document.querySelector('#CustomKit-Quickshop-Modal');
  const quickshop = modal?.querySelector('#custom-kit-quickshop');

  if (!modal || !quickshop) {
    console.warn('Custom kit quickshop modal unavailable');
    return;
  }

  const rawUrl = product.url || (product.handle ? `/products/${product.handle}` : null);
  if (!rawUrl) {
    console.warn('Custom quickview product URL missing');
    return;
  }
  const productUrl = new URL(rawUrl, window.location.origin);

  modal.classList.add('loading');
  if (typeof modal.show === 'function') {
    modal.show();
  } else {
    modal.setAttribute('open', 'open');
  }

  try {
    const response = await fetch(productUrl.pathname);
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const productInfo = html.querySelector('product-info');

    if (!productInfo) throw new Error('Product info not found in quickview response');

    productInfo.setAttribute('data-view-context', 'Quickview');
    quickshop.innerHTML = '';
    quickshop.appendChild(productInfo);

    const swatches = quickshop.querySelectorAll('.swatch-input__input');
    swatches.forEach((input, index) => {
      const originalId = input.id;
      const newId = `custom-kit-qv-${originalId || `swatch-${index}`}`;
      input.id = newId;
      const label = quickshop.querySelector(`label[for="${originalId}"]`);
      if (label) label.setAttribute('for', newId);
      input.setAttribute('data-quickview', 'true');
    });

    if (typeof window.reduceShadesPDP === 'function') {
      window.reduceShadesPDP(quickshop);
    }

    if (typeof window.initPDPSwatchesCarousel === 'function') {
      window.initPDPSwatchesCarousel(quickshop);
    }

    if (typeof window.initSwiper === 'function') {
      window.initSwiper('#custom-kit-quickshop');
    }

    if (typeof window.addProductBadges === 'function') {
      window.addProductBadges('#custom-kit-quickshop');
    }

    const galleryScript = quickshop.querySelector('#galleryScript');
    if (galleryScript) {
      try {
        eval(galleryScript.innerHTML);
      } catch (err) {
        console.error('Error executing gallery script', err);
      }
    }

    const badgeScript = quickshop.querySelector('#productBadgeScript');
    if (badgeScript) {
      try {
        eval(badgeScript.innerHTML);
      } catch (err) {
        console.error('Error executing badge script', err);
      }
    }

    const shadeScript = quickshop.querySelector('#shadeModalScript');
    if (shadeScript) {
      try {
        eval(shadeScript.innerHTML);
        if (typeof window.initShadeModal === 'function') {
          const source = inferQuickviewSource(product);
          const modalEl = quickshop.querySelector('[id^="ShadeList-Modal-"]');
          if (modalEl?.id) {
            const modalId = modalEl.id.replace('ShadeList-Modal-', '');
            window.initShadeModal(modalId, { source });
          } else {
            window.initShadeModal(undefined, { source });
          }
        }
      } catch (err) {
        console.error('Error executing shade modal script', err);
      }
    }

    const productJsonScript = quickshop.querySelector('script.product-json-data');
    const selectedProductJsonScript = quickshop.querySelector('script.selected-product-json-data');
    if (productJsonScript && selectedProductJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        const selectedProductData = JSON.parse(selectedProductJsonScript.textContent);
        document.dispatchEvent(new CustomEvent('quickviewLoaded', { detail: { productData, selectedProductData } }));
      } catch (err) {
        console.error('Unable to parse custom kit quickshop data', err);
      }
    }

    if (typeof window.initWaitlistModal === 'function') {
      const waitlistModal = document.querySelector('.waitlist-modal');
      window.initWaitlistModal(waitlistModal);
    }

    attachQuickviewCta(modal);

    modal.classList.remove('loading');
  } catch (error) {
    console.error('Error loading custom kit quickview:', error);
    modal.classList.remove('loading');
    if (typeof modal.hide === 'function') {
      modal.hide();
    } else {
      modal.removeAttribute('open');
    }
    quickshop.innerHTML = '';
    window.bagSpecialQuickviewState = null;
  }
}

function attachQuickviewCta(modal) {
  if (!modal) return;
  const ctaButton = modal.querySelector('.selected-shade-container button');
  if (!ctaButton) return;

  ctaButton.onclick = async (event) => {
    event.preventDefault();
    if (ctaButton.disabled) return;

    const productInfo = modal.querySelector('product-info');
    if (!productInfo) return;

    let variantId =
      productInfo.querySelector('input[type="radio"][data-variant-id]:checked')?.dataset?.variantId ||
      productInfo.querySelector('input[name="id"]')?.value;

    if (!variantId) {
      console.warn('Quickview CTA: variant not selected');
      return;
    }

    ctaButton.disabled = true;
    ctaButton.setAttribute('aria-disabled', 'true');

    try {
      await window.addToCartBySku(variantId, 'quickview');
      modal.hide?.();
    } catch (error) {
      console.error('Quickview CTA add failed:', error);
      ctaButton.disabled = false;
      ctaButton.setAttribute('aria-disabled', 'false');
    }
  };
}

window.loadKitQuickview = async function(productOptions = {}) {
  const controller = getCustomKitQuickviewController();
  if (controller?.openCustomKitQuickShop) {
    const quickviewProduct = normalizeKitQuickviewProduct(productOptions);
    if (quickviewProduct) {
      try {
        const source = inferQuickviewSource(productOptions);
        quickviewProduct.source = source;
        await controller.openCustomKitQuickShop(quickviewProduct);
        return;
      } catch (error) {
        console.error('Custom kit quickview failed; falling back to legacy loader', error);
      }
    }
  }

  return hydrateQuickviewFromPdp(productOptions);
};

window.getBagSpecialQuickviewScope = function(ctx) {
  if (!ctx) return null;
  const modal = document.querySelector(ctx.modalSelector || '#CustomKit-Quickshop-Modal');
  return modal?.querySelector('#custom-kit-quickshop') || null;
};

window.getBagSpecialVariantFromScope = function(scope) {
  if (!scope) return null;
  const selectedInput = scope.querySelector('input[type="radio"][data-variant-id]:checked');
  if (selectedInput) {
    return {
      id: Number(selectedInput.dataset.variantId),
      available: selectedInput.dataset.variantAvailable !== 'false',
      product_id: Number(selectedInput.dataset.productId || selectedInput.closest('[data-product-id]')?.dataset.productId || 0) || null,
    };
  }

  const hiddenInput = scope.querySelector('product-info input[name="id"]');
  if (hiddenInput?.value) {
    return {
      id: Number(hiddenInput.value),
      available: true,
      product_id: null,
    };
  }

  const selectedScript = scope.querySelector('script.selected-product-json-data');
  if (selectedScript?.textContent) {
    try {
      const data = JSON.parse(selectedScript.textContent);
      if (data) {
        return {
          id: Number(data.id || data.variant_id || data?.variant?.id),
          available: data.available !== false,
          product_id: Number(data.product_id || data?.product?.id || 0) || null,
        };
      }
    } catch (error) {
      console.warn('Unable to parse selected variant data', error);
    }
  }

  return null;
};

window.updateBagSpecialQuickviewCtaState = function(variant) {
  const ctx = window.bagSpecialQuickviewState;
  if (!ctx?.quickviewCta) return;

  const btn = ctx.quickviewCta;
  const availableLabel = btn.dataset.availableText || btn.textContent || '';
  const waitlistLabel = btn.dataset.joinText || btn.dataset.waitlistText || availableLabel;

  if (!variant || !variant.id) {
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.textContent = availableLabel;
    return;
  }

  const isAvailable = variant.available !== false;

  btn.disabled = false;
  btn.setAttribute('aria-disabled', 'false');
  btn.textContent = isAvailable ? availableLabel : waitlistLabel;
  btn.classList.toggle('join-waitlist', !isAvailable);
};

function setBagSpecialButtonAddedState(button) {
  if (!button) return () => {};
  const tile = button.closest('.recommended-product');
  tile?.classList.add('btn-added');
  button.setAttribute('aria-disabled', 'true');
  button.disabled = true;
  return () => {
    tile?.classList.remove('btn-added');
    button.removeAttribute('aria-disabled');
    button.disabled = false;
  };
}
window.setBagSpecialButtonAddedState = setBagSpecialButtonAddedState;

const BAG_SPECIAL_REMOVAL_ANIMATIONS = ['bag-special-collapse', 'bag-special-collapse-horizontal'];
const BAG_SPECIAL_REMOVAL_TIMEOUT = 1000;
const BAG_SPECIAL_ENTER_ANIMATION = 'bag-special-enter';
const BAG_SPECIAL_SLIDE_EVENT = 'bagSpecialSlideFinished';
const BAG_SPECIAL_SLIDE_TIMEOUT = 500;
let bagSpecialSlideObserverInitialized = false;

function ensureBagSpecialSlideObserver() {
  if (bagSpecialSlideObserverInitialized) return;
  bagSpecialSlideObserverInitialized = true;

  document.addEventListener(
    'animationend',
    (event) => {
      if (event.animationName !== BAG_SPECIAL_ENTER_ANIMATION) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.classList.contains('recommended-product')) return;
      if (!target.closest('#bag-specials-content')) return;
      document.dispatchEvent(new CustomEvent(BAG_SPECIAL_SLIDE_EVENT, { detail: { item: target } }));
    },
    true
  );
}

function waitForBagSpecialSlideFinished({ timeout = BAG_SPECIAL_SLIDE_TIMEOUT } = {}) {
  ensureBagSpecialSlideObserver();

  return new Promise((resolve) => {
    let resolved = false;

    const cleanup = (detail = null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      document.removeEventListener(BAG_SPECIAL_SLIDE_EVENT, handleSlideFinished);
      resolve(detail);
    };

    const handleSlideFinished = (event) => {
      cleanup(event?.detail || null);
    };

    const timeoutId = setTimeout(() => cleanup(null), timeout);
    document.addEventListener(BAG_SPECIAL_SLIDE_EVENT, handleSlideFinished);
  });
}
window.waitForBagSpecialSlideFinished = waitForBagSpecialSlideFinished;
ensureBagSpecialSlideObserver();

function animateBagSpecialRemoval(productContainer) {
  if (!productContainer) return Promise.resolve(() => {});

  return new Promise((resolve) => {
    let resolved = false;
    const cleanupClasses = () => {
      productContainer.classList.remove('bag-special--transitioning', 'bag-special--removing');
    };

    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      productContainer.removeEventListener('animationend', handleAnimationEnd);

      const siblings = Array.from(productContainer.parentElement?.children || []).filter(
        (child) => child !== productContainer && child.classList.contains('recommended-product')
      );

      // Calculate the width of the removed product + gap for sliding distance
      const removedWidth = productContainer.offsetWidth;
      const containerStyle = window.getComputedStyle(productContainer.parentElement || productContainer);
      const gap = parseFloat(containerStyle.gap) || 12;
      const slideDistance = removedWidth + gap;

      siblings.forEach((sibling) => {
        // Only animate if not already sliding or entering
        if (!sibling.classList.contains('bag-special--sliding') &&
            !sibling.classList.contains('bag-special--entering')) {
          sibling.style.setProperty('--slide-distance', `${slideDistance}px`);
          sibling.classList.add('bag-special--sliding');
          sibling.dataset.bagSpecialAnimated = 'true';
        }
      });

      // Small delay to ensure styles are applied, then remove from DOM
      requestAnimationFrame(() => {
        productContainer.remove();

        // Clean up after slide animation completes
        setTimeout(() => {
          siblings.forEach((sibling) => {
            sibling.classList.remove('bag-special--sliding');
            sibling.style.removeProperty('--slide-distance');
          });
        }, 400);
      });

      resolve(cleanupClasses);
    };

    const handleAnimationEnd = (event) => {
      if (event.target !== productContainer) return;
      if (!BAG_SPECIAL_REMOVAL_ANIMATIONS.includes(event.animationName)) return;
      cleanup();
    };

    const timeoutId = window.setTimeout(cleanup, BAG_SPECIAL_REMOVAL_TIMEOUT);
    productContainer.addEventListener('animationend', handleAnimationEnd);
    productContainer.classList.add('bag-special--transitioning');
    requestAnimationFrame(() => {
      productContainer.classList.add('bag-special--removing');
    });
  });
}
window.animateBagSpecialRemoval = animateBagSpecialRemoval;

function highlightBagSpecialCartItem(variantId, { consumePending = true } = {}) {
  const targetId = String(variantId || window.pendingBagSpecialCartVariant || '');
  if (!targetId) return;

  const selectItems = () => {
    let applied = false;
    const selectors = [
      '#CartDrawer-CartItems .cart-item[data-variant-id]',
      '#main-cart-items .cart-item[data-variant-id]',
      '.cart-item[data-variant-id]',
    ];
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((item) => {
        if (item.dataset.variantId !== targetId) return;
        applied = true;
        item.classList.remove('bag-special--recent');
        item.classList.remove('bag-special--incoming');
        void item.offsetWidth;

        // Add incoming class first to set initial hidden state
        item.classList.add('bag-special--incoming');

        // Wait for next frame to ensure styles are applied, then trigger reveal animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            item.classList.add('bag-special--recent');
          });
        });

        clearTimeout(item.__bagSpecialRevealTimer);
        item.__bagSpecialRevealTimer = setTimeout(() => {
          item.classList.remove('bag-special--recent');
          item.classList.remove('bag-special--incoming');
          item.__bagSpecialRevealTimer = null;
        }, 1600);
      });
    });
    if (applied && consumePending && window.pendingBagSpecialCartVariant === targetId) {
      window.pendingBagSpecialCartVariant = null;
    }
    return applied;
  };

  const attemptHighlight = (attempt = 0) => {
    const applied = selectItems();
    if (!applied && attempt < 2) {
      setTimeout(() => attemptHighlight(attempt + 1), 150);
    }
  };

  requestAnimationFrame(() => attemptHighlight(0));
}
window.highlightBagSpecialCartItem = highlightBagSpecialCartItem;

function applyPendingBagSpecialState(sectionHtml, variantId) {
  if (!sectionHtml || !variantId) return sectionHtml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const targets = doc.querySelectorAll(`.cart-item[data-variant-id="${variantId}"]`);
    if (!targets.length) return sectionHtml;
    targets.forEach((node) => {
      node.classList.add('bag-special--recent', 'bag-special--incoming');
    });
    return doc.body.innerHTML || sectionHtml;
  } catch (error) {
    console.warn('Failed to apply bag special pending state', error);
    return sectionHtml;
  }
}


async function handleBagSpecialQuickviewCtaClick(event) {
  const ctx = window.bagSpecialQuickviewState;
  if (!ctx || !ctx.requireQuickviewCta) return;

  event.preventDefault();

  const scope = ctx.quickviewScope || window.getBagSpecialQuickviewScope(ctx);
  const modal = document.querySelector(ctx.modalSelector || '#CustomKit-Quickshop-Modal');
  const cta = ctx.quickviewCta || event.currentTarget;

  let variant = ctx.selectedVariant;
  if (!variant) {
    variant = window.getBagSpecialVariantFromScope(scope);
    ctx.selectedVariant = variant;
  }

  if (!variant || !variant.id) {
    console.warn('Quickview CTA cannot add without a selected variant');
    return;
  }

  if (variant.available === false) {
    window.updateBagSpecialQuickviewCtaState(variant);
    return;
  }

  const productContainer = ctx.contextEl || ctx.button.closest('.recommended-product');
  ctx.processing = true;
  cta.disabled = true;
  cta.setAttribute('aria-disabled', 'true');
  const revertButtonState = setBagSpecialButtonAddedState(ctx.button);
  const hideQuickviewModal = () => {
    if (!modal) return;
    if (typeof modal.hide === 'function') {
      modal.hide();
    } else {
      modal.removeAttribute('open');
    }
  };

  let resetAnimation;
  try {
    const shouldBlock = await window.preventAddIfMaxedOut?.(variant.id, productContainer);
    if (shouldBlock) {
      ctx.processing = false;
      revertButtonState?.();
      cta.disabled = false;
      cta.removeAttribute('aria-disabled');
      window.updateBagSpecialQuickviewCtaState(variant);
      return;
    }

    ctx.button.dataset.variantId = variant.id;

    await new Promise(resolve => setTimeout(resolve, 300));

    hideQuickviewModal();

    await new Promise(resolve => {
      const handleModalClosed = (event) => {
        if (event?.detail === 'CustomKit-Quickshop-Modal' || event?.detail === 'Quickshop-Modal') {
          document.body.removeEventListener('modalClosed', handleModalClosed);
          setTimeout(resolve, 100);
        }
      };
      document.body.addEventListener('modalClosed', handleModalClosed);
      setTimeout(() => {
        document.body.removeEventListener('modalClosed', handleModalClosed);
        resolve();
      }, 500);
    });

    // Step 5: Start tile removal animation
    resetAnimation = await window.animateBagSpecialRemoval(productContainer);

    // Step 6: Add to cart immediately (slide-in happens in parallel)
    const bagSpecialSavings = window.getBagSpecialSavings?.(productContainer);

    if (bagSpecialSavings) {
      window.queueBagSpecialSavings?.(variant.id, bagSpecialSavings);
    }

    await window.addToCartBySku(variant.id, 'recommended', false, bagSpecialSavings);

    ctx.processing = false;
    cta.disabled = false;
    cta.removeAttribute('aria-disabled');
    window.bagSpecialQuickviewState = null;
  } catch (error) {
    console.error('Failed to add quickview product:', error);
    ctx.processing = false;
    revertButtonState?.();
    resetAnimation?.();
    cta.disabled = false;
    cta.removeAttribute('aria-disabled');
    window.updateBagSpecialQuickviewCtaState(variant);
  }
}

function initBagSpecialCustomKitQuickview(scope) {
  const ctx = window.bagSpecialQuickviewState;
  if (!ctx || !scope) return;

  const button = scope.querySelector('.selected-shade-container button');
  if (!button) return;

  ctx.quickviewScope = scope;

  const skipBinding = button.dataset.bagSpecialDirectAdd === 'true';

  if (!skipBinding) {
    button.onclick = null;
    if (button.__bagSpecialHandler) {
      button.removeEventListener('click', button.__bagSpecialHandler);
    }

    button.__bagSpecialHandler = handleBagSpecialQuickviewCtaClick;
    button.addEventListener('click', handleBagSpecialQuickviewCtaClick);
  }

  ctx.quickviewCta = button;
  ctx.processing = false;

  const initialVariant = window.getBagSpecialVariantFromScope(scope);
  ctx.selectedVariant = initialVariant;
  window.updateBagSpecialQuickviewCtaState(initialVariant);
}

document.addEventListener('customkitQuickviewLoaded', (event) => {
  const ctx = window.bagSpecialQuickviewState;
  if (!ctx || !ctx.requireQuickviewCta) return;

  const scope =
    event?.detail?.container ||
    window.getBagSpecialQuickviewScope(ctx);

  if (!scope) return;

  initBagSpecialCustomKitQuickview(scope);
});

window.initFreeSamplesSwiper = function(container) {
  if (!container || container.classList.contains('swiper-initialized')) return;
  new Swiper(container, {
    slidesPerView: 2,
    slidesPerGroup: 2,
    loop: false,
    navigation: {
      nextEl: container.querySelector('.free-sample-button-next'),
      prevEl: container.querySelector('.free-sample-button-prev'),
    },
    scrollbar: {
      el: container.querySelector('.swiper-scrollbar'),
      draggable: true,
    },
    breakpoints: {
      767: {
        slidesPerView: 2,
        slidesPerGroup: 2,
      },
    },
  });
};

window.initNewsletter = function(){
  const url = new URL(window.location.href);
  if (url.searchParams.has('customer_posted')) {
    window.history.replaceState({}, document.title, url.pathname);
  }

  const wrapper = document.querySelector('.newsletter-form');
  if (!wrapper) return;

  const emailInput = wrapper.querySelector("input[type='email']");
  if (!emailInput) return;

  const toggleConsent = () => {
    if (emailInput === document.activeElement || emailInput.value.trim() !== '') {
      wrapper.classList.add('show-consent');
    } else {
      wrapper.classList.remove('show-consent');
    }
  };

  emailInput.addEventListener('focus', toggleConsent);
  emailInput.addEventListener('input', toggleConsent);

  const consentCheckbox = document.querySelector("input[name='consent']");

  if (consentCheckbox) {
    consentCheckbox.addEventListener('invalid', function (e) {
      e.target.setCustomValidity('Please accept terms and conditions listed.');
    });

    consentCheckbox.addEventListener('input', function (e) {
      e.target.setCustomValidity('');
    });
  }

  const submitButton = emailInput?.form?.querySelector('.newsletter-submit');
  const errorMsg = wrapper.querySelector('.field__error-message');

  const isValidEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  emailInput?.addEventListener('blur', function () {
    const email = emailInput.value.trim();
    // Forms that render a consent checkbox gate the submit button on it, so never override that here
    const consentGate = wrapper.querySelector("input[name='consentCheckbox']");
    if (isValidEmail(email)) {
      submitButton.disabled = consentGate ? !consentGate.checked : false;
      errorMsg.classList.add('hidden');
      emailInput.classList.remove('error');
      errorMsg.style.display = 'none;'
    } else {
      submitButton.disabled = true;
      if (email.length > 0) {
        errorMsg.classList.remove('hidden');
        emailInput.classList.add('error');
        errorMsg.style.display = 'block';
      } else {
        errorMsg.style.display = 'none;'
        emailInput.classList.remove('error');
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', window.initNewsletter);


// Capture hash before Shopify scripts can remove it
if (window.location.hash === '#reviews') {
  sessionStorage.setItem('scrollToReviews', 'true');
}

document.addEventListener('DOMContentLoaded', () => {
  const shouldScroll = sessionStorage.getItem('scrollToReviews') === 'true';

  if (!shouldScroll) return;

  const scrollToReviews = () => {
    const target = document.querySelector('#yotpo-reviews-main-widget');
    if (target && target.offsetHeight > 0) {
      sessionStorage.removeItem('scrollToReviews');
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setTimeout(scrollToReviews, 300);
    }
  };

  scrollToReviews();
});


// Capture hash before Shopify scripts can remove it
if (window.location.hash === '#reviews') {
  sessionStorage.setItem('scrollToReviews', 'true');
}

document.addEventListener('DOMContentLoaded', () => {
  const shouldScroll = sessionStorage.getItem('scrollToReviews') === 'true';

  if (!shouldScroll) return;

  const scrollToReviews = () => {
    const target = document.querySelector('#yotpo-reviews-main-widget');
    if (target && target.offsetHeight > 0) {
      sessionStorage.removeItem('scrollToReviews');
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setTimeout(scrollToReviews, 300);
    }
  };

  scrollToReviews();
});

document.addEventListener('click', async (e) => {
  // Check if clicking on product image, product title, or CTA button
  const productImageLink = e.target.closest('.recommended-product .product-image-link');
  const productTitle = e.target.closest('.recommended-product .product-title');
  const btn = e.target.closest('.recommended-cta-button');

  let fromKit = false;
  if (btn?.classList?.contains('kit-special')) {
    fromKit = true
  }

  // If clicking product image or product title, trigger quickview
  if ((productImageLink || productTitle) && !btn) {
    e.preventDefault();
    const productContainer = (productImageLink || productTitle).closest('.recommended-product');
    const ctaButton = productContainer?.querySelector('.recommended-cta-button');
    if (ctaButton) {
      // Simulate click on the CTA button to open quickview
      ctaButton.click();
    }
    return;
  }

  if (!btn || !btn.classList.contains('add')) return;

  const productContainer = btn.closest('.recommended-product') || btn.parentElement || e.target.parentElement;
  const productJsonEl = productContainer?.querySelector('script.bag-special-product-json');
  let quickviewProductData = null;
  if (productJsonEl?.textContent) {
    try {
      quickviewProductData = JSON.parse(productJsonEl.textContent);
    } catch (error) {
      console.error('Failed to parse bag special product data', error);
    }
  }

  const requiresShadeSelectionAttr = btn.dataset.requiresShadeSelection;
  const requiresShadeSelection = requiresShadeSelectionAttr === undefined ? true : requiresShadeSelectionAttr === 'true';

  const quickviewUrl = btn.dataset.quickviewUrl;
  const quickviewModalType = btn.dataset.quickviewModal || 'standard';

  const totalVariantCount = Array.isArray(quickviewProductData?.variants)
    ? quickviewProductData.variants.length
    : null;
  const hasVisibleSwatches = productContainer?.querySelectorAll('.bag-special-swatch-input').length > 0;
  const isSingleProductLayout = productContainer?.closest('.recommendations-content--single') !== null;

  const shouldOpenQuickview =
    Boolean(quickviewUrl && requiresShadeSelection) &&
    !isSingleProductLayout &&
    !(totalVariantCount !== null && totalVariantCount <= 1 && !hasVisibleSwatches);

  if (shouldOpenQuickview) {
    e.preventDefault();

    const productId = btn.dataset.productId ? String(btn.dataset.productId) : null;
    const modalSelector = quickviewModalType === 'custom-kit' ? '#CustomKit-Quickshop-Modal' : '#Quickshop-Modal';

    window.bagSpecialQuickviewState = {
      button: btn,
      productId,
      contextEl: productContainer,
      processing: false,
      source: 'checkout special',
      modalSelector,
      requireQuickviewCta: quickviewModalType === 'custom-kit',
      selectedVariant: null,
      quickviewCta: null,
      quickviewScope: null,
    };

    if (quickviewModalType === 'custom-kit') {
      if (typeof window.loadKitQuickview !== 'function') {
        console.warn('Custom kit quickview unavailable');
        window.bagSpecialQuickviewState = null;
        return;
      }
      const quickviewPayload = {
        url: quickviewUrl,
        handle: productContainer?.dataset?.productHandle,
        productData: quickviewProductData
      };
      window.loadKitQuickview(quickviewPayload);
    } else {
      if (typeof window.loadQuickview !== 'function') {
        console.warn('Quickview unavailable');
        window.bagSpecialQuickviewState = null;
        return;
      }
      window.loadQuickview({ url: quickviewUrl });
    }
    return;
  }

  const variantId = btn.dataset.variantId;
  if (!variantId) {
    return;
  }

  btn.setAttribute('aria-disabled', 'true');
  const shouldBlock = await window.preventAddIfMaxedOut(variantId, productContainer);
  if (shouldBlock) {
    btn.removeAttribute('aria-disabled');
    return;
  }

  let resetAnimation;
  try {
    productContainer.classList.add('btn-added');
    btn.disabled = true;

    resetAnimation = await window.animateBagSpecialRemoval(productContainer);

    const bagSpecialSavings = window.getBagSpecialSavings?.(productContainer);

    // Queue the savings to be applied when cart data updates
    if (bagSpecialSavings) {
      window.queueBagSpecialSavings?.(variantId, bagSpecialSavings);
    }

    await window.addToCartBySku(variantId, 'recommended', fromKit, bagSpecialSavings);
  } catch (error) {
    console.error('Failed to add recommended product:', error);
    resetAnimation?.();
    btn.removeAttribute('aria-disabled');
    btn.disabled = false;
    productContainer.classList.remove('btn-added');
  }
});

document.addEventListener('swatchChange', async (event) => {
  const ctx = window.bagSpecialQuickviewState;
  if (!ctx || ctx.processing) return;

  const variant = event?.detail?.selectedVariant;
  if (!variant) return;

  const variantProductId = variant.product_id ? String(variant.product_id) : null;
  if (ctx.productId && variantProductId && ctx.productId !== variantProductId) return;

  const modalSelector = ctx.modalSelector || '#Quickshop-Modal';
  const quickshopModal = document.querySelector(modalSelector);
  if (!quickshopModal) return;

  if (ctx.modalSelector === '#CustomKit-Quickshop-Modal' && ctx.requireQuickviewCta) {
    ctx.selectedVariant = variant;
    window.updateBagSpecialQuickviewCtaState(variant);
    return;
  }

  ctx.processing = true;
  const productContainer = ctx.contextEl || ctx.button.closest('.recommended-product');

  const shouldBlock = await window.preventAddIfMaxedOut?.(variant.id, productContainer);
  if (shouldBlock) {
    ctx.processing = false;
    return;
  }

  const hideQuickshopModal = () => {
    if (!quickshopModal) return;
    if (typeof quickshopModal.hide === 'function') {
      quickshopModal.hide();
    } else if (quickshopModal instanceof HTMLElement) {
      quickshopModal.removeAttribute('open');
    }
  };

  const revertButtonState = setBagSpecialButtonAddedState(ctx.button);
  let resetAnimation;
  try {
    ctx.button.dataset.variantId = variant.id;

    // Step 1: Checkmark is already added via setBagSpecialButtonAddedState above
    // Step 2: Brief delay to show checkmark behind modal
    await new Promise(resolve => setTimeout(resolve, 300));

    // Step 3: Close modal
    hideQuickshopModal();

    // Step 4: Wait for modal close animation
    await new Promise(resolve => {
      const handleModalClosed = (event) => {
        if (event?.detail === 'Quickshop-Modal' || event?.detail === 'CustomKit-Quickshop-Modal') {
          document.body.removeEventListener('modalClosed', handleModalClosed);
          setTimeout(resolve, 100);
        }
      };
      document.body.addEventListener('modalClosed', handleModalClosed);
      // Fallback timeout
      setTimeout(() => {
        document.body.removeEventListener('modalClosed', handleModalClosed);
        resolve();
      }, 500);
    });

    resetAnimation = await window.animateBagSpecialRemoval(productContainer);

    const bagSpecialSavings = window.getBagSpecialSavings?.(productContainer);

    // Queue the savings to be applied when cart data updates
    if (bagSpecialSavings) {
      window.queueBagSpecialSavings?.(variant.id, bagSpecialSavings);
    }

    await window.addToCartBySku(variant.id, 'recommended', false, bagSpecialSavings);

    ctx.processing = false;
    window.bagSpecialQuickviewState = null;
  } catch (error) {
    console.error('Failed to add quickview product:', error);
    revertButtonState?.();
    resetAnimation?.();
    ctx.processing = false;
  }
});

document.addEventListener('DOMContentLoaded', () => {
  document.body?.addEventListener('modalClosed', (event) => {
    if (event?.detail === 'Quickshop-Modal' || event?.detail === 'CustomKit-Quickshop-Modal') {
      window.bagSpecialQuickviewState = null;
    }
  });
});

// consent settings footer link
document.addEventListener('click', (e) => {
  const link = e.target.closest('a');
  if (!link) return;

  const url = new URL(link.href, window.location.origin);
  const isMenuItem  = link.matches('.list-menu__item--consent-settings a');
  const isHrefExact = url.pathname === '/' && url.searchParams.has('consent-settings');
  const hasParam    = url.searchParams.has('consent-settings');

  if (isMenuItem || isHrefExact || hasParam) {
    e.preventDefault();
    Optanon.ToggleInfoDisplay();
  }
});

document.addEventListener('DOMContentLoaded', function () {
  const isLoggedIn = window.customerIsLoggedIn;
  const lastState = localStorage.getItem('customerState');
  const currentState = isLoggedIn ? 'logged_in' : 'logged_out';

  if (lastState !== currentState) {
    localStorage.setItem('customerState', currentState);
    if (isLoggedIn) {
      document.dispatchEvent(new CustomEvent('customer:loggedin'));
    }

    fetch(window.routes.cart_url + '.js')
    .then(r => r.json())
    .then(cart => {
      const sampleItems = cart.items.filter(item =>
        item.properties['_freesample'] === 'true'
      );

      if (sampleItems.length === 0) return;

      sampleItems.forEach(item => {
        window.removeFreeSample(item.variant_id);
      });
    });
  }
});

window.getRenderedCartSections = function(sections = ['cart-drawer', 'cart-icon-bubble']) {
 const root = window.Shopify.routes?.root || '';
  const localePrefix = root.endsWith('/') ? root.slice(0, -1) : root;

  const params = new URLSearchParams();
  params.append('sections', sections.join(','));

  return fetch(`${localePrefix}/?${params.toString()}`, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  }).then(res => {
    if (!res.ok) throw new Error(`Sections request failed: ${res.status}`);
    return res.json();
  });
}

document.addEventListener('customer:loggedin', () => {
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';

  const responseSections = [
    'cart-icon-bubble',
    'cart-drawer',
  ];

  config.body = JSON.stringify({
    sections: responseSections.join(',')
  })

  fetch(window.routes.cart_url + '.js', config)
    .then(r => r.json())
    .then(cart => {
      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      window.getRenderedCartSections()
        .then((sections) => {
          cartEl?.renderContents({ sections }, false, false);
        })
        .catch((err) => {
          console.error('Failed to refresh cart drawer:', err);
        });
    })
});

document.addEventListener('DOMContentLoaded', function () {
  const hash = window.location.hash;

  if (hash) {
    const targetElement = document.querySelector(hash);

    if (targetElement) {
      const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offset = 150;
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
    }
  }
});

window.getCurrentTarteCartData = async function(options = {}) {
  const { forceFetch = false } = options;
  const cachedCart = window.getTarteCartData?.();
  const hasFullCartData = (
    Array.isArray(cachedCart?.items) &&
    typeof cachedCart.item_count === 'number' &&
    typeof cachedCart.total_price === 'number'
  );

  if (!forceFetch && hasFullCartData) {
    return cachedCart;
  }

  try {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((res) => res.json());
    if (typeof window.updateTarteCartData === 'function') {
      await window.updateTarteCartData(cart);
    }
    return cart;
  } catch (err) {
    console.error('[Cart] Failed to fetch cart data:', err);
    return null;
  }
};

window.getVariantQuantityInCart = async function (variantId, cartData) {
  try {
    const cart = cartData || await window.getCurrentTarteCartData();
    if (!Array.isArray(cart?.items)) return 0;

    if (typeof buildTarteCartVariantQuantityMap === 'function') {
      return getTarteVariantQuantityFromMap(buildTarteCartVariantQuantityMap(cart), variantId);
    }

    return cart.items.reduce((sum, item) => {
      return item.variant_id === Number(variantId) ? sum + item.quantity : sum;
    }, 0);
  } catch (err) {
    console.error('[Cart] Failed to fetch cart data:', err);
    return 0;
  }
};

const SHOP_DEFAULT_MAX = window.shopDefaultMaxQuantity;
const BUNDLE_DEFAULT_MAX = window.shopDefaultBundleMaxQuantity;

window.showQuantityLimitModal = function (shadeName, maxQuantity) {
  const modal = document.querySelector('#QuantityLimitReached');
  if (!modal) return;

  const product = modal.querySelector('#MaxedOutProductName');
  if (product) product.textContent = shadeName || 'this product';

  const quantityLimit = modal.querySelector('#MaxQuantity');
  if (quantityLimit) quantityLimit.textContent = `to ${maxQuantity}`;

  modal.show();
};

function getProductAndVariantMetafieldsData(containerEl, selectedVariant) {
  if (!containerEl) {
    return null;
  }
  const metafieldsData = containerEl.querySelector('[data-product-variant-metafields-json]');

  if (!metafieldsData) {
    console.warn('Metafields script not found.');
    return null;
  }

  try {
    const parsed = JSON.parse(metafieldsData.textContent);
    const productMetafields = parsed.product || {};
    const variantMetafields = parsed.variants || {};
    const variantData = variantMetafields[selectedVariant] || {};

    const isValidQuantity = (val) => {
      return typeof val !== 'boolean'
        && val !== ''
        && val !== 'null'
        && val != null
        && !isNaN(Number(val))
        && Number(val) > 0;
    };

    let maxQuantity;

    if (isValidQuantity(variantData?.max_quantity)) {
      maxQuantity = variantData.max_quantity;
    } else if (isValidQuantity(productMetafields.metafields?.max_quantity)) {
      maxQuantity = productMetafields.metafields.max_quantity;
    } else {
      // use defaults
      if (productMetafields.metafields?.bundle_set_handles && BUNDLE_DEFAULT_MAX) {
        maxQuantity = BUNDLE_DEFAULT_MAX;
      } else {
        maxQuantity = isValidQuantity(SHOP_DEFAULT_MAX) ? SHOP_DEFAULT_MAX : 6;
      }
    }

    const maxQuantityNumber = Number(maxQuantity);
    return { maxQuantityNumber, productTitle: productMetafields.title, variantShade: variantData.title || '' };
  } catch (err) {
    console.error('Failed to parse metafields JSON:', err);
    return null;
  }
}

window.preventAddIfMaxedOut = async (variantId, containerEl) => {
  if (!variantId || !containerEl) {
    // failed because function was called but param not set correctly, do not prevent atb on error
    return false;
  }
  const metafields = getProductAndVariantMetafieldsData(containerEl, variantId);
    if ( !metafields) {
    // failed because metafields were not availabe, do not prevent atb on error
    return false;
  }
  const currentQty = await window.getVariantQuantityInCart(variantId);
  const maxQty = metafields?.maxQuantityNumber ?? SHOP_DEFAULT_MAX;
  const productTitle = `${metafields.productTitle} - ${metafields.variantShade}`;

  if (currentQty >= maxQty) {
    window.showQuantityLimitModal(productTitle, maxQty);
    return true;
  }

  return false;
};


let swiperInitialized = false;

function initPopTartesSwiper() {
  const swiperEl = document.querySelector('.pop-tartes-swiper');
  if (!swiperEl || swiperInitialized) return;

  const drawerVisible = document.querySelector('.menu-drawer')?.offsetParent !== null;
  if (!drawerVisible) return;

  const slides = swiperEl.querySelectorAll('.swiper-slide');
  const nextBtn = swiperEl.querySelector('.swiper-button-next');
  const prevBtn = swiperEl.querySelector('.swiper-button-prev');

  const totalSlides = slides.length;
  
  prevBtn.style.display = 'none';

  if (totalSlides <= 1) {
    nextBtn.style.display = 'none';
    prevBtn.style.display = 'none';
    return;
  }

  const swiper = new Swiper(swiperEl, {
    slidesPerView: 3.5,
    slidesPerGroup: 1,
    spaceBetween: 12,
    grabCursor: true,
    loop: false,
    navigation: {
      nextEl: nextBtn,
      prevEl: prevBtn,
    },
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    threshold: 10,
  });

  swiper.update();

  nextBtn.style.display = '';

  swiperInitialized = true;

  nextBtn.addEventListener('click', function () {
    if (prevBtn.style.display === 'none' || getComputedStyle(prevBtn).display === 'none') {
      prevBtn.style.display = 'flex';
    }
  });
}

document.querySelector('#Details-menu-drawer-container')?.addEventListener('toggle', (e) => {
  if (e.target.open) {
    requestAnimationFrame(() => {
      initPopTartesSwiper();
    });
  }
});

function parseDisplayPrice(rawText) {
  if (!rawText) return NaN;
  const cleaned = rawText.replace(/[^\d.,]/g, '');

  // Remove leading/trailing dots or commas that aren't part of a number
  const trimmed = cleaned.replace(/^[.,]+|[.,]+$/g, '');

  if (!trimmed) return NaN;

  const lastComma = trimmed.lastIndexOf(',');
  const lastDot = trimmed.lastIndexOf('.');
  let normalized = trimmed;

  // normalize currencies with decimals and commas
  if (lastComma !== -1 && lastDot !== -1) {
    const decimalIsComma = lastComma > lastDot;
    if (decimalIsComma) {
      normalized = trimmed.replace(/\./g, '').replace(/,/g, '.');
    } else {
      normalized = trimmed.replace(/,/g, '');
    }
  } else if (lastComma !== -1) {
    const parts = trimmed.split(',');
    const tail = parts[parts.length - 1];
    if (/^\d{2}$/.test(tail)) {
      normalized = trimmed.replace(/,/g, '.');
    } else {
      normalized = trimmed.replace(/,/g, '');
    }
  } else {
    normalized = trimmed;
  }

  const val = parseFloat(normalized);
  return Number.isFinite(val) ? val : NaN;
}

function formatStrikethroughPrice(el) {
  const rawText = el.textContent.trim();
  if (!rawText) return;

  // Skip reformatting for zero-decimal currencies where dot/comma thousands
  // separators can be misinterpreted by Intl.NumberFormat under the wrong locale.
  // Regios DOPP and Liquid already format these correctly.
  const activeCurrency =
    (window.Shopify && Shopify.currency && Shopify.currency.active) ||
    document.documentElement.getAttribute('data-shopify-currency') ||
    'USD';
  const { maximumFractionDigits: currencyDecimals } = new Intl.NumberFormat('en', { style: 'currency', currency: activeCurrency }).resolvedOptions();
  if (currencyDecimals === 0) return;

  const val = parseDisplayPrice(rawText);
  if (!Number.isFinite(val)) return;

  const locale =
    (window.Shopify && Shopify.locale) ||
    document.documentElement.lang ||
    navigator.language ||
    'en-US';

  const base = new Intl.NumberFormat(locale, { style: 'currency', currencyDisplay: 'narrowSymbol', currency: activeCurrency });
  const { maximumFractionDigits, locale: resolvedLocale } = base.resolvedOptions();

  const isWhole = Math.round(val * 100) % 100 === 0;
  const minFD = isWhole ? 0 : Math.min(2, maximumFractionDigits ?? 2);
  const maxFD = maximumFractionDigits ?? 2;

  const formatter = new Intl.NumberFormat(resolvedLocale, {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: activeCurrency,
    minimumFractionDigits: minFD,
    maximumFractionDigits: maxFD,
  });

  el.textContent = formatter.format(val);
}


function watchAndFormatStrikethroughPrices() {
  const strikeEls = document.querySelectorAll('.price__container .price-item');
  if (!strikeEls.length) return;

  strikeEls.forEach((el) => {
    let debounceTimer = null;
    let attempts = 0;
    const maxAttempts = 5;

    const observer = new MutationObserver(() => {
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        attempts++;
        formatStrikethroughPrice(el);

        if (attempts >= maxAttempts) {
          observer.disconnect();
        }
      }, 150);
    });

    observer.observe(el, {
      characterData: true,
      subtree: true,
      childList: true,
    });

    formatStrikethroughPrice(el);
  });

  document.dispatchEvent(new CustomEvent('priceFormatted:regios'));
}

// Handle clicks on elements with data-open-signin-modal attribute
document.addEventListener('click', function(e) {
  const target = e.target;
  if (target && target.hasAttribute('data-open-signin-modal')) {
    e.preventDefault();
    const modal = document.querySelector('#SignIn-Modal');
    if (modal) modal.show();
  }
});

// Handle opening findMyShadeModal via URL params -- ?openFindMyShadeModal=true&productHandle=product-name or ?openFindMyShadeModal=true
document.addEventListener('DOMContentLoaded', function() {
  const urlParams = new URLSearchParams(window.location.search);
  const openFindMyShadeModal = urlParams.get('openFindMyShadeModal');

  if (openFindMyShadeModal === 'true') {
    const productHandle = urlParams.get('productHandle');
    const opener = document.querySelector('.product__find-my-shade-modal-opener');

    const cleanupUrlParams = function() {
      if (window.history.replaceState) {
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('openFindMyShadeModal');
        newUrl.searchParams.delete('productHandle');
        window.history.replaceState({}, '', newUrl);
      }
    };

    if (productHandle) {
      // If productHandle is specified, fetch and open modal for that specific product
      const root = window.Shopify?.routes?.root || '';
      const modalUrl = `${root}products/${productHandle}?view=modal-find-my-shade-menu`;
      const modalID = '#findMyShadeModal';

      const existingModal = document.querySelector(modalID);
      if (existingModal && existingModal.parentNode) {
        existingModal.parentNode.removeChild(existingModal);
      }

      fetch(modalUrl, {
        cache: 'no-cache'
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const modalDialog = html.querySelector(modalID);

          console.log('Modal HTML received:', modalDialog ? 'Found' : 'Not found');
          console.log('Modal options in HTML:', {
            vto: html.querySelector('.find-my-shade__vto-modal-opener') !== null,
            quiz: html.querySelector('.find-my-shade__option-quiz') !== null,
            match: html.querySelector('findation-widget') !== null
          });

          if (modalDialog) {
            const modalScripts = modalDialog.querySelectorAll('script');
            modalScripts.forEach(script => {
              const newScript = document.createElement('script');
              if (script.src) {
                newScript.src = script.src;
              } else {
                newScript.textContent = script.textContent;
              }
              document.body.appendChild(newScript);
            });

            document.body.appendChild(modalDialog);
            if (typeof modalDialog.show === 'function') {
              modalDialog.show();
            }

            cleanupUrlParams();
          }
        })
        .catch((error) => {
          console.error('Error opening findMyShadeModal:', error);
          cleanupUrlParams();
        });
    } else if (opener) {
      // No productHandle specified, trigger the find my shade button on current page
      const button = opener.querySelector('button');
      if (button) {
        button.click();
        cleanupUrlParams();
      }
    }
  }
});

window.reduceShadesPDP = (container) => {
  const rootEl = container || document;
  const productInfo = rootEl.querySelector('product-info');
  const productInfoParent = productInfo.parentElement;

  const isPDPView = productInfoParent.classList.contains('product-section') || productInfoParent.classList.contains('quickshop-modal');
  if (isPDPView) {
    const reducedShadeContainers = productInfo.querySelectorAll('[data-reduced-shades*="pdp"]');
    if (!reducedShadeContainers) return;
    reducedShadeContainers.forEach((containerEl) => {
      const reducedShades = containerEl.querySelectorAll('.swiper-slide[data-include-reduced="false"]');
      reducedShades.forEach((shade) => {
        shade.remove();
      });
    });
  }
}

// =============================================================================
// TARTE CART DATA - LocalStorage Management
// =============================================================================

const TARTE_CART_DATA_KEY = 'tarte_cart_data';

window.customKitCountdownState = window.customKitCountdownState || {
  entries: [],
  expiresAt: null,
  durationMs: null,
  intervalId: null,
};

/**
 * Get the current tarte_cart_data from localStorage
 * @returns {Object|null} The stored cart data or null if not found
 */
window.getTarteCartData = function() {
  try {
    const data = localStorage.getItem(TARTE_CART_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error('Error reading tarte_cart_data from localStorage:', e);
    return null;
  }
};

/**
 * Set the tarte_cart_data in localStorage
 * @param {Object} data - The cart data to store
 */
window.setTarteCartData = function(data) {
  try {
    localStorage.setItem(TARTE_CART_DATA_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving tarte_cart_data to localStorage:', e);
  }
};

const TARTE_LOCALE_PATH_PATTERN = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i;

function getLocalePrefixFromPath(path) {
  if (typeof path !== 'string') return null;

  try {
    const pathname = new URL(path, window.location.origin).pathname;
    const match = pathname.match(TARTE_LOCALE_PATH_PATTERN);
    return match ? match[0].replace(/\/$/, '') : '';
  } catch (e) {
    const match = path.match(TARTE_LOCALE_PATH_PATTERN);
    return match ? match[0].replace(/\/$/, '') : '';
  }
}

function getCurrentLocalePrefix() {
  if (window.Shopify?.routes?.root) {
    const prefix = getLocalePrefixFromPath(window.Shopify.routes.root);
    if (prefix !== null) return prefix;
  }

  if (window.routes?.cart_url) {
    const prefix = getLocalePrefixFromPath(window.routes.cart_url);
    if (prefix !== null) return prefix;
  }

  return getLocalePrefixFromPath(window.location.pathname) || '';
}

function applyLocalePrefixToPath(pathname, localePrefix) {
  let cleanPath = pathname.replace(TARTE_LOCALE_PATH_PATTERN, '');
  cleanPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

  if (!localePrefix) return cleanPath || '/';
  return `${localePrefix}${cleanPath === '/' ? '' : cleanPath}`;
}

/**
 * Sync custom_kit_url to the active storefront locale.
 * @param {Object} item - Cart item with custom_kit_url property
 * @returns {Object} - Item with synced custom_kit_url
 */
window.syncCustomKitUrlLocale = function(item) {
  if (!item.custom_kit_url) return item;

  try {
    const isRelative = !/^https?:\/\//i.test(item.custom_kit_url);
    const customUrl = new URL(item.custom_kit_url, window.location.origin);
    customUrl.pathname = applyLocalePrefixToPath(customUrl.pathname, getCurrentLocalePrefix());
    item.custom_kit_url = isRelative
      ? customUrl.pathname + customUrl.search + customUrl.hash
      : customUrl.toString();
  } catch (e) {
    // If URL parsing fails, return item unchanged
  }

  return item;
};

/**
 * Ensure a custom kit URL carries ck_line_key so the builder opens in edit mode.
 * Preserves locale and returns a relative URL when the input was relative.
 * @param {string} urlStr
 * @param {string} lineKey
 * @returns {string}
 */
window.ensureCkLineKey = function(urlStr, lineKey) {
  if (!urlStr || !lineKey) return urlStr;
  try {
    const isRelative = !/^https?:\/\//i.test(urlStr);
    const url = new URL(urlStr, window.location.origin);
    // Always set/replace ck_line_key to avoid stale keys from previous edits
    url.searchParams.set('ck_line_key', lineKey);
    return isRelative ? url.pathname + url.search : url.toString();
  } catch (e) {
    return urlStr;
  }
};

/**
 * Update tarte_cart_data with new cart data from server response
 * Uses full cart data from cart mutations when available, otherwise fetches cart.js.
 * Dynamically preserves any custom properties not in the cart.js payload
 * @param {Object} eventData - The cart data from the event (may be partial, e.g., from add.js)
 */
window.updateTarteCartData = async function(eventData) {
  try {
    const incomingCartData = eventData?.cartData || eventData?.detail?.cartData || eventData;
    const hasFullCartData = (cartData) => (
      cartData &&
      Array.isArray(cartData.items) &&
      typeof cartData.item_count === 'number' &&
      typeof cartData.total_price === 'number'
    );

    // Get existing stored data to preserve custom properties
    const existingData = window.getTarteCartData();
    const existingItemsByKey = {};
    if (existingData?.items) {
      existingData.items.forEach((item) => {
        if (item.key) {
          existingItemsByKey[item.key] = item;
        }
      });
    }

    let cartData = hasFullCartData(incomingCartData) ? incomingCartData : null;
    if (!cartData) {
      // no-store: avoid a stale cached /cart.js after add/remove cycles (TARTE-18078)
      const response = await fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store' });
      cartData = await response.json();
    }

    // Build items array, preserving any existing custom properties
    const items = (cartData.items || []).map((item) => {
      const existingItem = existingItemsByKey[item.key] || {};
      // Spread existing first to preserve custom props, then cart data on top
      const mergedItem = { ...existingItem, ...item };

      // Extract bag special savings from properties if it exists
      if (mergedItem.properties?._bag_special_savings) {
        mergedItem.bag_special_savings = Number(mergedItem.properties._bag_special_savings);
      }

      return mergedItem;
    });

    // If eventData contains item updates (e.g., from add.js), merge by key
    if (incomingCartData?.items?.length) {
      incomingCartData.items.forEach((eventItem) => {
        const index = items.findIndex((item) => item.key === eventItem.key);
        if (index !== -1) {
          // Spread existing, then event data on top (includes custom props)
          items[index] = { ...items[index], ...eventItem };

          // Extract bag special savings from properties if it exists
          if (items[index].properties?._bag_special_savings) {
            items[index].bag_special_savings = Number(items[index].properties._bag_special_savings);
          }
        }
      });
    }

    // Sync custom_kit_url to the active storefront locale for each item
    items.forEach((item) => window.syncCustomKitUrlLocale(item));

    // Use raw cart data, override items with merged array, and avoid storing rendered sections HTML.
    const { sections, ...cartDataWithoutSections } = cartData;
    const mergedCartData = { ...cartDataWithoutSections, items };
    window.setTarteCartData(mergedCartData);

    // Dispatch event to notify listeners that cart data has been updated
    // (The event listener will apply pending bag special savings)
    document.dispatchEvent(new CustomEvent('tarte:cart-data-updated', {
      detail: { cartData: mergedCartData }
    }));
  } catch (e) {
    console.error('Error updating tarte_cart_data:', e);
  }
};

/**
 * Clear tarte_cart_data from localStorage
 */
window.clearTarteCartData = function() {
  try {
    localStorage.removeItem(TARTE_CART_DATA_KEY);
  } catch (e) {
    console.error('Error clearing tarte_cart_data from localStorage:', e);
  }
};

/**
 * Update cart edit links to use custom_kit_url when available
 * If an item has a custom_kit_url, sets it as the href and removes onclick so browser handles navigation
 * Also updates cart-item__name and cart-item__link with the URL (without query params)
 */
window.updateCartEditLinks = function() {
  const cartData = typeof window.getTarteCartData === 'function' ? window.getTarteCartData() : null;
  const cartItems = cartData?.items || [];

  // Create a map of item keys to their custom_kit_url
  const itemUrlMap = {};
  cartItems.forEach((item) => {
    if (!item?.key) return;

    // Prefer normalized top-level URL, fall back to properties
    let customUrl = item.custom_kit_url || item.properties?._custom_kit_url;

    // If we only have a properties URL, sync it to the active storefront locale
    if (!item.custom_kit_url && customUrl && typeof window.syncCustomKitUrlLocale === 'function') {
      const synced = window.syncCustomKitUrlLocale({ ...item, custom_kit_url: customUrl });
      customUrl = synced?.custom_kit_url || customUrl;
    }

    if (customUrl) {
      itemUrlMap[item.key] = customUrl;
    }
  });

  // Update edit links that have a matching custom_kit_url
  const editLinks = document.querySelectorAll('.cart-item__edit-link');
  editLinks.forEach((link) => {
    const itemKey = link.dataset.itemKey;
    const row = link.closest('.cart-item');
    let customKitUrl = itemKey ? itemUrlMap[itemKey] : null;

    // Fallback to data attribute rendered by Liquid if localStorage data is missing
    if (!customKitUrl && row?.dataset?.customKitUrl) {
      customKitUrl = row.dataset.customKitUrl;
    }

    if (!customKitUrl) return;

    // Best-effort locale sync even when we only have DOM data
    let normalizedUrl = customKitUrl;
    if (typeof window.syncCustomKitUrlLocale === 'function') {
      try {
        const synced = window.syncCustomKitUrlLocale({ custom_kit_url: customKitUrl });
        normalizedUrl = synced?.custom_kit_url || customKitUrl;
      } catch (e) {
        // ignore and continue
      }
    }

    normalizedUrl = window.ensureCkLineKey(normalizedUrl, itemKey);

    // Normalize to absolute for URL parsing while preserving relative href
    const parsedUrl = new URL(normalizedUrl, window.location.origin);
    const urlWithoutParams = parsedUrl.origin + parsedUrl.pathname;

    link.href = parsedUrl.pathname + parsedUrl.search;
    link.removeAttribute('onclick');

    // Also update the cart-item__name and cart-item__link in the same row (without query params)
    const nameLink = row?.querySelector('.cart-item__name');
    const imageLink = row?.querySelector('.cart-item__link');
    if (nameLink) {
      nameLink.href = urlWithoutParams;
    }
    if (imageLink) {
      imageLink.href = urlWithoutParams;
    }
  });
};

window.updateCustomKitBundleLinks = function() {
  const cartData = typeof window.getTarteCartData === 'function' ? window.getTarteCartData() : null;
  const cartItems = cartData?.items || [];

  // Update all custom kit cart item rows
  const cartItemRows = document.querySelectorAll('.cart-item[data-is-custom-kit="true"]');

  cartItemRows.forEach((row) => {
    const itemKey = row.dataset.itemKey;

    // Find the cart item with matching key and get its custom_kit_url from localStorage
    const cartItem = cartItems.find(item => item.key === itemKey);
    let customKitUrl = cartItem?.custom_kit_url || cartItem?.properties?._custom_kit_url || row.dataset.customKitUrl;

    // Sync locale whenever possible
    if (customKitUrl && typeof window.syncCustomKitUrlLocale === 'function') {
      try {
        const synced = window.syncCustomKitUrlLocale({ ...(cartItem || {}), custom_kit_url: customKitUrl });
        customKitUrl = synced?.custom_kit_url || customKitUrl;
      } catch (e) {
        // ignore and continue
      }
    }

    customKitUrl = window.ensureCkLineKey(customKitUrl, itemKey);

    if (!customKitUrl) return;

    row.dataset.customKitUrl = customKitUrl;

    const imageLink = row.querySelector('.cart-item__link');
    if (imageLink) {
      imageLink.href = customKitUrl;
    }

    const nameLink = row.querySelector('.cart-item__name');
    if (nameLink) {
      nameLink.href = customKitUrl;
    }

    const editLink = row.querySelector('.cart-item__edit-link');
    if (editLink) {
      editLink.href = customKitUrl;
      editLink.removeAttribute('onclick');
    }
  });
};

/**
 * Update build kit cart item edit links to navigate back to the build kit page.
 * Appends bk_line_key (the item's cart key) and bk_variant (the item's variant ID)
 * as query params so the build kit page can restore state and auto-open the right QV.
 */
window.updateBuildKitEditLinks = function() {
  const cartItemRows = document.querySelectorAll('.cart-item[data-build-kit-url]');
  if (!cartItemRows.length) return;

  cartItemRows.forEach((row) => {
    const buildKitUrl = row.dataset.buildKitUrl;
    if (!buildKitUrl) return;

    const itemKey = row.dataset.itemKey;
    const variantId = row.dataset.variantId;
    const buildKitOriginKey = row.dataset.buildKitOriginKey;
    const buildKitEditGroup = row.dataset.buildKitEditGroup;

    const url = new URL(buildKitUrl);
    if (row.dataset.isBuildKit === 'true') {
      if (itemKey) url.searchParams.set('bk_line_key', itemKey);
      if (variantId) url.searchParams.set('bk_variant', variantId);
    } else if (buildKitEditGroup) {
      url.searchParams.set('bk_edit_group', buildKitEditGroup);
      if (variantId) url.searchParams.set('bk_variant', variantId);
    } else if (buildKitOriginKey) {
      url.searchParams.set('bk_requalified_origin', buildKitOriginKey);
      if (variantId) url.searchParams.set('bk_variant', variantId);
    }
    const editUrl = url.toString();

    const editLink = row.querySelector('.cart-item__edit-link');
    if (editLink) {
      editLink.href = editUrl;
      editLink.removeAttribute('onclick');
    }
  });
};

/**
 * Update cart item rows with added_timestamp from tarte_cart_data
 */
window.updateCartItemTimestamps = function() {
  const cartData = window.getTarteCartData();
  if (!cartData?.items) return;

  let cartItemRows = document.querySelectorAll('.cart-item[data-is-custom-kit="true"]');

  if (cartItemRows.length === 0) {
    cartItemRows = document.querySelectorAll('.cart-item[data-item-key]');
  }
  cartItemRows.forEach((row) => {
    const itemKey = row.dataset.itemKey;
    const item = cartData.items.find((i) => i.key === itemKey);
    if (item?.added_timestamp) {
      row.dataset.addedTimestamp = item.added_timestamp;
    }

    // Only update countdown timer attributes if they exist in localStorage
    // Don't delete them if they're not in localStorage (they may be rendered from Liquid)
    if (item?.countdown_timer_time_amount) {
      row.dataset.countdownTimerAmount = item.countdown_timer_time_amount;
    }

    if (item?.countdown_timer_copy) {
      row.dataset.countdownTimerCopy = item.countdown_timer_copy;
    }
  });
};

window.getCustomKitCountdownDurationMs = function(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return null;
  // Treat small values as minutes, larger values as seconds for flexibility.
  return value <= 60 ? value * 60 * 1000 : value * 1000;
};

window.formatCustomKitCountdownValue = function(ms) {
  const safeMs = Math.max(0, Number(ms) || 0);
  const minutes = Math.floor(safeMs / 60000);
  const seconds = Math.floor((safeMs % 60000) / 1000);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

window.stopCustomKitCountdownInterval = function() {
  if (window.customKitCountdownState?.intervalId) {
    clearInterval(window.customKitCountdownState.intervalId);
    window.customKitCountdownState.intervalId = null;
  }
};

window.resetCustomKitCountdownUI = function() {
  window.stopCustomKitCountdownInterval();
  if (Array.isArray(window.customKitCountdownState?.entries)) {
    window.customKitCountdownState.entries.forEach((entry) => {
      if (entry?.countdown) entry.countdown.hidden = true;
      if (entry?.timeEl) entry.timeEl.textContent = '';
    });
  }
  window.customKitCountdownState = {
    entries: [],
    expiresAt: null,
    durationMs: null,
    intervalId: null,
  };
};

window.renderCustomKitCountdown = function() {
  const state = window.customKitCountdownState;
  if (!state?.entries?.length || !state.expiresAt) return;

  const remainingMs = Math.max(0, state.expiresAt - Date.now());
  const formatted = window.formatCustomKitCountdownValue(remainingMs);

  state.entries.forEach((entry) => {
    if (entry?.timeEl) {
      entry.timeEl.textContent = formatted;
    }
  });

  if (remainingMs <= 0) {
    window.stopCustomKitCountdownInterval();
  }
};

window.startCustomKitCountdownInterval = function() {
  if (!window.customKitCountdownState?.entries?.length) return;
  window.stopCustomKitCountdownInterval();
  window.customKitCountdownState.intervalId = window.setInterval(() => {
    window.renderCustomKitCountdown();
  }, 1000);
};


window.updateCustomKitCountdownTimers = function() {
  let kitRows = Array.from(document.querySelectorAll('.cart-item[data-is-custom-kit="true"]'));

  if (kitRows.length === 0) {
    kitRows = Array.from(document.querySelectorAll('.cart-item[data-item-key]'));
  }

  // Check if the OOS header is visible (appears when checkout validation fails)
  const oosHeader = document.querySelector('.cart-items-oos__wrapper');
  const hasOOSAlert = oosHeader && oosHeader.offsetParent !== null;

  if (hasOOSAlert) {
    window.resetCustomKitCountdownUI();
    return;
  }

  // Find entries that should show countdown (have timer enabled)
  const entries = kitRows
    .map((row) => {
      const countdown = row.querySelector('[data-custom-kit-countdown]');
      const timeEl = countdown?.querySelector('[data-countdown-time]');
      if (!countdown || !timeEl) return null;

      const label = countdown.querySelector('[data-countdown-label]');
      const timestamp = Date.parse(row.dataset.addedTimestamp || '');
      const amount = Number(row.dataset.countdownTimerAmount);

      // Only include items that have both timestamp and timer amount (means countdown is enabled)
      if (!Number.isFinite(timestamp) || !Number.isFinite(amount) || amount <= 0) {
        return null;
      }

      return {
        row,
        countdown,
        timeEl,
        label,
        defaultLabel: label?.dataset?.defaultText || '',
        addedTimestamp: timestamp,
        timerAmount: amount,
      };
    })
    .filter(Boolean);

  if (!entries.length) {
    window.resetCustomKitCountdownUI();
    return;
  }

  const durationEntry = entries.find((entry) => entry.timerAmount && entry.timerAmount > 0);
  const durationMs = durationEntry ? window.getCustomKitCountdownDurationMs(durationEntry.timerAmount) : null;
  if (!durationMs) {
    window.resetCustomKitCountdownUI();
    return;
  }

  const timestamps = entries
    .map((entry) => entry.addedTimestamp)
    .filter((value) => Number.isFinite(value));
  const baseTimestamp = timestamps.length ? Math.max(...timestamps) : Date.now();
  const expiresAt = baseTimestamp + durationMs;

  let labelText = durationEntry?.defaultLabel || '';
  let textColor = '';

  // First try to get from data attributes (persists through cart updates)
  const rowWithCountdown = kitRows.find(row => row.dataset.countdownTimerCopy);
  if (rowWithCountdown?.dataset.countdownTimerCopy) {
    labelText = rowWithCountdown.dataset.countdownTimerCopy;
  }
  if (rowWithCountdown?.dataset.countdownTimerTextColor) {
    textColor = rowWithCountdown.dataset.countdownTimerTextColor;
  }

  // Fall back to localStorage if data attributes not found
  if (!labelText || !textColor) {
    const cartData = window.getTarteCartData();
    if (cartData?.items) {
      const itemWithCountdown = cartData.items.find(item => item.countdown_timer_copy);
      if (itemWithCountdown?.countdown_timer_copy && !labelText) {
        labelText = itemWithCountdown.countdown_timer_copy;
      }
      if (itemWithCountdown?.countdown_timer_text_color && !textColor) {
        textColor = itemWithCountdown.countdown_timer_text_color;
      }
    }
  }

  entries.forEach((entry) => {
    entry.countdown.hidden = false;

    // Apply text color if specified
    if (textColor) {
      entry.countdown.style.color = textColor;
    }

    if (entry.label) {
      entry.label.textContent = labelText;
    }
  });

  window.customKitCountdownState = {
    entries,
    expiresAt,
    durationMs,
    intervalId: window.customKitCountdownState?.intervalId || null,
  };

  window.renderCustomKitCountdown();
  window.startCustomKitCountdownInterval();
};

window.resetCustomKitCountdownOnInteraction = function() {
  const cartData = window.getTarteCartData();
  if (!cartData?.items) return;

  // Find items with countdown timer enabled
  const itemsWithTimer = cartData.items.filter(item => item.countdown_timer_time_amount);
  if (!itemsWithTimer.length) return;

  // Update added_timestamp for all items with countdown timer
  const newTimestamp = new Date().toISOString();
  let updated = false;

  itemsWithTimer.forEach(item => {
    if (item.added_timestamp) {
      item.added_timestamp = newTimestamp;
      updated = true;
    }
  });

  if (updated) {
    window.setTarteCartData(cartData);
    window.updateCartItemTimestamps();
    window.updateCustomKitCountdownTimers();
  }
};

window.updateCustomKitCountdownLabels = function() {
  const labels = document.querySelectorAll('[data-countdown-label]');
  if (!labels.length) return;

  // First try to get countdown copy from data attributes (persists through locale changes)
  const kitRows = document.querySelectorAll('.cart-item[data-is-custom-kit="true"]');
  let countdownCopy = '';

  if (kitRows.length > 0) {
    for (const row of kitRows) {
      const copy = row.dataset.countdownTimerCopy;
      if (copy) {
        countdownCopy = copy;
        break;
      }
    }
  }

  // Fallback to localStorage if data attribute not found
  if (!countdownCopy) {
    const cartData = window.getTarteCartData();
    if (cartData?.items) {
      const itemWithCopy = cartData.items.find(item => item.countdown_timer_copy);
      if (itemWithCopy?.countdown_timer_copy) {
        countdownCopy = itemWithCopy.countdown_timer_copy;
      }
    }
  }

  labels.forEach((label) => {
    const fallback = label.dataset?.defaultText || '';
    const copy = countdownCopy || fallback;
    if (copy) {
      label.textContent = copy;
    }
  });
};


window.updateCustomKitPromoMessages = function() {
  const cartData = window.getTarteCartData();
  if (!cartData?.items) return;

  let kitRows = document.querySelectorAll('.cart-item[data-is-custom-kit="true"]');

  if (kitRows.length === 0) {
    kitRows = document.querySelectorAll('.cart-item[data-item-key]');
  }
  kitRows.forEach((row) => {
    const promoEl = row.querySelector('[data-custom-kit-promo-message]');

    if (!promoEl) return;

    const target = promoEl.querySelector('[data-custom-kit-promo-text]');

    // First try to get from data attribute (persists through cart updates)
    let message = row.dataset.bagPromoMessage || '';

    // Fall back to localStorage if data attribute not found
    if (!message) {
      const itemKey = row.dataset.itemKey;
      const item = cartData.items.find((i) => i.key === itemKey);
      message = item?.bag_promo_message || '';
    }

    if (message && target) {
      target.textContent = message;
      promoEl.hidden = false;
    } else {
      if (target) target.textContent = '';
      promoEl.hidden = true;
    }
  });
};

window.updateCustomKitOriginalTotals = function() {
  const kitPriceBlocks = document.querySelectorAll('[data-kit-price-block]');

  kitPriceBlocks.forEach((priceBlock) => {
    const originalPriceTarget = priceBlock.querySelector('[data-kit-original-price]');
    const kitRow = priceBlock.closest('.cart-item');

    if (!originalPriceTarget || !kitRow) return;

    const kitPrices = kitRow.querySelectorAll('[data-kit-item-price]');
    if (!kitPrices.length) {
      priceBlock.hidden = true;
      originalPriceTarget.hidden = true;
      originalPriceTarget.textContent = '';
      return;
    }

    const total = Array.from(kitPrices).reduce((sum, priceNode) => {
      const value = Number(priceNode.dataset.kitItemPrice || priceNode.getAttribute('data-kit-item-price'));
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);

    if (total > 0) {
      originalPriceTarget.textContent = window.TarteCurrencyFormatter.formatCents(total, {
        trimTrailingZeros: true,
      });
      originalPriceTarget.hidden = false;
      priceBlock.hidden = false;
    } else {
      priceBlock.hidden = true;
      originalPriceTarget.hidden = true;
      originalPriceTarget.textContent = '';
    }
  });
}

window.customKitSavingsBannerUpdateId = window.customKitSavingsBannerUpdateId || 0;

window.updateCustomKitSavingsBanner = async function() {
  const updateId = ++window.customKitSavingsBannerUpdateId;
  const isStaleUpdate = () => updateId !== window.customKitSavingsBannerUpdateId;
  const banner = document.querySelector('.cart-ck-banner');

  if (!banner) {
    return;
  }

  const cartData = window.getTarteCartData();

  if (!cartData?.items || cartData.items.length === 0) {
    banner.hidden = true;
    return;
  }

  // First try to find custom kit items from DOM data attributes
  const cartRows = document.querySelectorAll('.cart-item[data-enable-add-another-ck-banner]');
  let customKitItems = [];
  let customKitRows = []; // Keep track of the DOM rows too

  if (cartRows.length > 0) {
    // If we have DOM elements with the data attribute, use those
    cartRows.forEach(row => {
      const itemKey = row.dataset.itemKey;
      const flagValue = row.dataset.enableAddAnotherCkBanner;

      // Check if the flag is truthy (could be "true" string or true boolean)
      if (flagValue === 'true' || flagValue === true) {
        const item = cartData.items.find(i => i.key === itemKey);
        if (item) {
          customKitItems.push(item);
          customKitRows.push(row); // Store the corresponding row
        }
      }
    });
  } else {
    // Fallback to checking localStorage
    customKitItems = cartData.items.filter(item =>
      item.enable_add_another_ck_banner === true || item.properties?._enable_add_another_ck_banner === 'true' || item.properties?._enable_add_another_ck_banner === true
    );
  }

  // Hide banner if no custom kits or banner not enabled
  if (!customKitItems.length) {
    banner.hidden = true;
    return;
  }

  const currentCountry = (window?.Shopify?.country || 'US').toUpperCase();

  // Get original locale from cart items
  const firstKit = customKitItems[0];
  const firstKitComponent = Array.isArray(firstKit.item_components) ? firstKit.item_components[0] : null;
  const firstKitProperties = {
    ...(firstKitComponent?.properties || {}),
    ...(firstKit.properties || {})
  };

  const originalLocale = (firstKit?.ck_original_locale ||
                        firstKitProperties._ck_original_locale ||
                        currentCountry).toUpperCase();

  let totalSavings = 0;
  let needsConversion = originalLocale && originalLocale !== currentCountry;

  // Calculate total savings from cart items (in original locale amounts)
  customKitRows.forEach(row => {
    const rowSavings = row.dataset.customKitTotalSavings;
    if (rowSavings) {
      const savings = Number(rowSavings);
      if (!isNaN(savings)) {
        totalSavings += savings;
      }
    }
  });

  // If no savings found in data attributes, try properties
  if (totalSavings === 0) {
    customKitItems.forEach((kitMarkerItem, index) => {
      const kitRow = customKitRows[index];
      let storedSavings = 0;

      if (kitRow?.dataset.customKitTotalSavings) {
        const parsed = Number(kitRow.dataset.customKitTotalSavings);
        storedSavings = !isNaN(parsed) ? parsed : 0;
      } else if (kitMarkerItem.custom_kit_total_savings) {
        const parsed = Number(kitMarkerItem.custom_kit_total_savings);
        storedSavings = !isNaN(parsed) ? parsed : 0;
      } else if (kitMarkerItem.properties?._custom_kit_total_savings) {
        const parsed = Number(kitMarkerItem.properties._custom_kit_total_savings);
        storedSavings = !isNaN(parsed) ? parsed : 0;
      }

      totalSavings += storedSavings;

      // Add any additional bag special savings
      const bundleKey = kitMarkerItem.bundle_key || kitMarkerItem.properties?._bundle_key;
      if (bundleKey) {
        const kitAllItems = cartData.items.filter(item =>
          item.bundle_key === bundleKey || item.properties?._bundle_key === bundleKey
        );
        const additionalCheckoutSavings = kitAllItems.reduce((sum, item) => {
          return sum + (Number(item.bag_special_savings) || 0);
        }, 0);
        totalSavings += additionalCheckoutSavings;
      }
    });
  }

  // If locale changed, convert the savings using the parent variant price source of truth.
  let conversionFailed = false;

  if (needsConversion) {
    let conversionRatio = 1;
    let hasConversion = false;
    const parentVariantId = firstKitProperties._parent_id ||
                            firstKit.variant_id ||
                            firstKit.id ||
                            customKitRows[0]?.dataset.variantId;

    if (parentVariantId && typeof window.fetchVariantPriceForCountry === 'function') {
      try {
        const [originalParentPrice, currentParentPrice] = await Promise.all([
          window.fetchVariantPriceForCountry(parentVariantId, originalLocale),
          window.fetchVariantPriceForCountry(parentVariantId, currentCountry)
        ]);

        if (isStaleUpdate()) return;

        if (originalParentPrice && currentParentPrice) {
          conversionRatio = currentParentPrice / originalParentPrice;
          hasConversion = true;
        }
      } catch (err) {
        if (isStaleUpdate()) return;
        console.warn('[CK Savings] Failed to convert custom kit savings. Variant:', parentVariantId, 'Original locale:', originalLocale, 'Current locale:', currentCountry, err);
      }
    }

    if (isStaleUpdate()) return;

    if (!hasConversion) {
      console.warn('[CK Savings] Missing parent variant prices for conversion. Variant:', parentVariantId, 'Original locale:', originalLocale, 'Current locale:', currentCountry);
      conversionFailed = true;
    }

    // Apply conversion ratio to the stored savings
    if (hasConversion) {
      totalSavings = Math.round(totalSavings * conversionRatio);
    }
  }

  const standaloneBagSpecialSavings = cartData.items.reduce((sum, item) => {
    // Only count bag special savings if the item isn't part of a custom kit
    const isPartOfKit = customKitItems.some(kit =>
      kit.properties?._bundle_key &&
      item.properties?._bundle_key === kit.properties._bundle_key
    );

    if (!isPartOfKit && item.bag_special_savings) {
      return sum + Number(item.bag_special_savings);
    }
    return sum;
  }, 0);

  totalSavings += standaloneBagSpecialSavings;

  // get config from first custom kit item (already declared above)
  const firstKitRow = document.querySelector(`.cart-item[data-item-key="${firstKit.key}"]`);

  const showSavingsBanner = firstKitRow?.dataset.showSavingsBanner !== 'false' ||
                            (firstKit.show_savings_banner !== false && firstKit.show_savings_banner !== 'false') ||
                            (firstKit.properties?._show_savings_banner !== false && firstKit.properties?._show_savings_banner !== 'false');
  const messageTemplate = firstKitRow?.dataset.savingsBannerMessage ||
                          firstKit.savings_banner_message ||
                          firstKit.properties?._savings_banner_message ||
                          '{{amount}}';
  const discountType = firstKitRow?.dataset.discountType ||
                       firstKit.discount_type ||
                       firstKit.properties?._discount_type;

  const amountEl = banner.querySelector('.cart-ck-banner__savings-message-amount');
  const copyEl = banner.querySelector('.cart-ck-banner__savings-message-copy');

  if (!amountEl) {
    banner.hidden = true;
    return;
  }

  const bannerCopy = firstKitRow?.dataset.ckBannerMessageCopy ||
                     firstKit.ck_banner_message_copy ||
                     firstKit.properties?._ck_banner_message_copy ||
                     '';

  if (conversionFailed && showSavingsBanner && discountType !== 'none') {
    amountEl.textContent = '';
    if (copyEl) copyEl.textContent = '';
    banner.hidden = true;
    return;
  }

  const customKitProductId = firstKit.ck_bundle_product_id || firstKit.properties?._bundle_product_id || firstKit.product_id;

  // Read max_quantity from data attribute first (persists through checkout), then fallback to localStorage
  const maxKitsFromData = firstKitRow?.dataset.maxQuantity;
  const maxKits = maxKitsFromData ? Number(maxKitsFromData) : (firstKit.max_quantity || 0);

  // Count unique custom kits by bundle key
  const uniqueBundleKeys = new Set();
  cartData.items.forEach(item => {
    const bundleProductId = item.ck_bundle_product_id || item.properties?._bundle_product_id;
    const bundleKey = item.bundle_key || item.properties?._bundle_key;

    if (bundleProductId && bundleKey && String(bundleProductId) === String(customKitProductId)) {
      uniqueBundleKeys.add(bundleKey);
    } else if (item.product_id === customKitProductId) {
      uniqueBundleKeys.add(item.key || item.id);
    }
  });

  const customKitCount = uniqueBundleKeys.size;
  const isMaxReached = maxKits && maxKits > 0 && customKitCount >= maxKits;

  const addAnotherButton = banner.querySelector('.cart-ck-banner__btn');
  if (addAnotherButton) {
    const customKitUrl = firstKitRow?.dataset.customKitUrl ||
                         firstKit.custom_kit_url ||
                         firstKit.properties?._custom_kit_url;

    if (customKitUrl) {
      try {
        const storedUrl = new URL(customKitUrl, window.location.origin);

        const locale = window.Shopify?.locale || 'en';
        const country = window.Shopify?.country || 'US';

        // build clean path and locale prefix
        let cleanPath = storedUrl.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?\//, '/');
        cleanPath = cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath;

        const localizedPath = country === 'US'
          ? cleanPath
          : `/${locale}-${country.toLowerCase()}${cleanPath}`;

        addAnotherButton.href = localizedPath;
      } catch (e) {
        console.error('[Add Another Kit Button] Error:', e);
        // Fallback: just use the pathname without query string
        addAnotherButton.href = customKitUrl.split('?')[0];
      }
    } else {
      addAnotherButton.style.display = 'none';
      return;
    }

    if (isMaxReached) {
      addAnotherButton.style.display = 'none';
    } else {
      addAnotherButton.style.display = '';
    }
  }

  console.log('[Savings Banner Decision]', { discountType, showSavingsBanner, totalSavings });

  if (isStaleUpdate()) return;

  if (discountType === 'none' || !showSavingsBanner) {
    // show message without calculation
    const message = messageTemplate.replace('{{amount}}', '').trim();
    amountEl.textContent = message;
    if (copyEl) copyEl.textContent = '';
    console.log('[Savings Banner] Showing banner without calculation');
    banner.hidden = false;
  } else if (showSavingsBanner) {
    // Show banner with savings calculation (even if totalSavings is 0)
    const roundedDown = Math.floor(totalSavings / 100) * 100;
    const formattedSavings = window.TarteCurrencyFormatter.formatCents(roundedDown, {
      trimTrailingZeros: true,
    });
    amountEl.textContent = formattedSavings;
    if (copyEl) copyEl.textContent = bannerCopy;
    console.log('[Savings Banner] Showing banner with savings:', formattedSavings);
    banner.hidden = false;
  } else {
    console.log('[Savings Banner] Hiding banner');
    banner.hidden = true;
  }
};

window.updateCartElmWithTarteCartData = async function() {
  try {
    window.updateCartEditLinks();
    window.updateCustomKitBundleLinks();
    window.updateBuildKitEditLinks();
    window.updateCartItemTimestamps();
    window.updateCustomKitOriginalTotals();
    window.updateCustomKitCountdownTimers();
    window.updateCustomKitCountdownLabels();
    window.updateCustomKitPromoMessages();
    await window.updateCustomKitSavingsBanner();
  } catch (e) {
    console.error('Error updating cart custom kit elements:', e);
  }
}

// Listen for cart data updates and cart drawer renders to refresh edit links and timestamps
document.addEventListener('tarte:cart-data-updated', async () => {
  // Apply any pending bag special savings when cart data is updated
  if (window.pendingBagSpecialSavings?.length > 0 && typeof window.applyPendingBagSpecialSavings === 'function') {
    await window.applyPendingBagSpecialSavings();
  }

  await window.updateCartElmWithTarteCartData()
});

document.addEventListener('cart-drawer:rendered', async () => {
  // Apply any pending bag special savings when cart drawer renders
  if (window.pendingBagSpecialSavings?.length > 0 && typeof window.applyPendingBagSpecialSavings === 'function') {
    await window.applyPendingBagSpecialSavings();
  }

  await window.updateCartElmWithTarteCartData();
});

document.addEventListener('cart-items:updated', async () => {
  await window.updateCartElmWithTarteCartData()
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.updateCartElmWithTarteCartData);
} else {
  window.updateCartElmWithTarteCartData();
}

/**
 * Initialize the tarte_cart_data listener
 * Subscribes to cart update events to automatically persist cart data
 */
window.initTarteCartDataListener = function() {
  // Subscribe to cart updates via pub/sub system
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cartData) {
        window.updateTarteCartData(event.cartData);
      }

      if (event?.countdown_timer_copy) {
        window.setCustomKitCountdownCopy(event.countdown_timer_copy);
      }
    });
  }

  // Initial fetch and store on page load
  window.updateTarteCartData();
};

// Initialize the listener when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.initTarteCartDataListener);
} else {
  window.initTarteCartDataListener();
}

// =============================================================================
// Custom Kit Countdown Timer - User Interaction Reset
// =============================================================================

/**
 * Initialize countdown timer reset on user interactions
 * Resets the countdown timer whenever user interacts with the page
 */
window.initCustomKitCountdownInteractionReset = function() {
  let isActive = false;

  const handleInteraction = function() {
    if (!isActive) return;

    // Reset countdown timer immediately on cart interaction
    window.resetCustomKitCountdownOnInteraction();
  };

  // Check if countdown timers exist and should be monitored
  const checkCountdownActive = function() {
    const cartData = window.getTarteCartData();
    isActive = cartData?.items?.some(item => item.countdown_timer_time_amount) || false;
  };

  checkCountdownActive();

  document.addEventListener('tarte:cart-data-updated', checkCountdownActive);
  document.addEventListener('cart-drawer:rendered', checkCountdownActive);

  // Reset countdown timer on cart interactions
  document.addEventListener('tarte:cart-data-updated', handleInteraction); // Items added/removed/updated
  document.addEventListener('cart-drawer:rendered', handleInteraction);
};

// Initialize interaction reset when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.initCustomKitCountdownInteractionReset);
} else {
  window.initCustomKitCountdownInteractionReset();
}
